<template>
  <div
    data-testid="conference-route"
    :data-viewer-active-tracks="String(viewerActiveTrackCount)"
    :data-viewer-audio="conference.viewer.trackState.audio ? 'on' : 'off'"
    :data-viewer-camera="conference.viewer.trackState.camera ? 'on' : 'off'"
    :data-viewer-screenshare="conference.viewer.trackState.screenshare ? 'on' : 'off'"
    :data-media-runtime-status="runtimeError ?? runtimeStatus ?? ''"
    class="bg-conference-surface h-screen overflow-x-auto overflow-y-hidden text-[#222]"
  >
    <div data-testid="conference-layout-root" class="flex h-screen min-w-275 overflow-hidden">
      <aside
        data-testid="conference-layout-left-rail"
        class="border-conference-line-strong bg-conference-structure flex h-full w-68.75 shrink-0 flex-col border-r text-white"
      >
        <MediaControlBar
          :audio-enabled="conference.viewer.trackState.audio"
          :audio-input-options="audioInputOptions"
          :audio-output-options="audioOutputOptions"
          :audio-unavailable="!canToggleAudio"
          :camera-enabled="conference.viewer.trackState.camera"
          :camera-unavailable="!canToggleCamera"
          :display-name="conference.viewer.displayName"
          :selected-audio-input="selectedAudioInput"
          :selected-audio-output="selectedAudioOutput"
          @toggle-audio="handleToggleAudio"
          @toggle-camera="handleToggleCamera"
          @update:selected-audio-input="setSelectedAudioInput"
          @update:selected-audio-output="setSelectedAudioOutput"
        />
        <ConferenceSignalBar
          :active-sound-name="activeSoundName"
          :conference-slug="conference.conference.slug"
          :feedback-visibility="conference.conference.feedbackVisibility"
          :viewer="conference.viewer"
          @notice="appendConferenceLog($event.label, $event.detail)"
        />
        <ConferenceTeacherControlsBar
          v-if="conference.viewer.role === 'teacher'"
          :conference-slug="conference.conference.slug"
          :current-room-slug="conference.currentRoom.slug"
          :global-permissions="conference.conference.permissions"
          :has-breakout-rooms="hasBreakoutRooms"
          :rooms="conference.rooms"
          :viewer="{
            cloakedRoomName: conference.viewer.cloakedRoomName,
            role: conference.viewer.role,
          }"
          @notice="appendConferenceLog($event.label, $event.detail)"
        />

        <section
          data-testid="conference-layout-room-roster"
          :style="roomRosterStyle"
          class="border-conference-line-strong bg-conference-surface-muted text-conference-ink-strong relative flex min-h-0 flex-1 flex-col border-b"
        >
          <div class="border-conference-line-soft bg-conference-structure border-b px-2.5 py-1.5 text-white">
            <div class="flex items-center justify-between gap-2">
              <div>
                <p class="text-[11px] font-semibold tracking-[0.2em] uppercase">Rooms</p>
                <p class="text-[10px] text-white/75">{{ conference.rooms.length }} available</p>
              </div>

              <div class="flex items-center gap-1">
                <CIconButton
                  size="sm"
                  tone="neutral"
                  :aria-pressed="roomSwitchingEnabled"
                  aria-label="Enable room switching"
                  :disabled="!canToggleRoomSwitching"
                  :title="roomSwitchingEnabled ? 'Disable room switching' : 'Enable room switching'"
                  @click="void toggleRoomSwitching()"
                >
                  <span aria-hidden="true" class="text-[11px] leading-none">⇄</span>
                </CIconButton>
                <CIconButton
                  size="sm"
                  tone="neutral"
                  aria-label="Close breakout rooms"
                  :disabled="!canCloseBreakoutRooms"
                  :title="hasBreakoutRooms ? 'Close and delete all breakout rooms' : 'No breakout rooms'"
                  @click="void handleCloseBreakoutRooms()"
                >
                  <span aria-hidden="true" class="text-[12px] leading-none">×</span>
                </CIconButton>
              </div>
            </div>
          </div>

          <nav
            data-testid="room-navigation-list"
            aria-label="Conference rooms"
            tabindex="-1"
            :class="[
              'bg-conference-surface-muted min-h-0 flex-1 overflow-y-auto',
              draggedParticipantIds.length > 0 ? 'select-none' : '',
            ]"
          >
            <ConferenceRoomSection
              v-for="room in conference.rooms"
              :key="room.slug"
              :activity="conference.roomActivityByRoomSlug[room.slug] ?? null"
              :can-navigate="canNavigateRooms"
              :chat-visible="canUseChatPanel"
              :confetti-highlighted-participant-ids="confettiHighlightedParticipantIds"
              :drag-source-room-slugs="dragSourceRoomSlugs"
              :dragged-participant-ids="draggedParticipantIds"
              :feedback-visibility="conference.conference.feedbackVisibility"
              :highlighted-participant-ids="highlightedParticipantIds"
              :participants="conference.participantsByRoomSlug[room.slug] ?? []"
              :room="room"
              :selected-participant-ids="selectedParticipantIds"
              :speaking-participant-ids="speakingParticipantIds"
              :switching-room="switchingRoom"
              :viewer-cloaked="showViewerCloakMarker"
              :viewer-participant-id="conference.viewer.id"
              :viewer-is-teacher="conference.viewer.role === 'teacher'"
              @adjust-participation="(_roomSlug, participantId, delta) => void handleAdjustParticipation(participantId, delta)"
              @close-room="void handleCloseBreakoutRoom($event)"
              @drop-participants="void handleRoomDrop($event)"
              @open-participant-menu="openParticipantContextMenu"
              @open-room-menu="openRoomContextMenu"
              @participant-dragend="handleParticipantDragEnd"
              @participant-dragstart="handleParticipantDragStart"
              @select-room="emit('select-room', $event)"
              @select-participant="handleParticipantSelection"
            />
          </nav>

          <div
            v-if="showRailChatPanel"
            aria-hidden="true"
            class="absolute inset-x-0 -bottom-0.75 h-1.5 cursor-n-resize transition hover:bg-black/50"
            @pointerdown="startRoomRosterResize"
          />
        </section>

        <section v-if="showRailChatPanel" class="border-conference-line-strong flex min-h-0 flex-1 flex-col border-t">
          <div v-if="showChatBlackout" aria-hidden="true" class="min-h-0 flex-1 bg-black" data-testid="chat-disabled-blackout" />
          <ChatPanel
            v-else
            dockable
            :chat-state="chatState"
            :conference-slug="conference.conference.slug"
            :requested-thread-key="requestedChatThreadKey"
            :current-room-slug="conference.currentRoom.slug"
            :rooms="conference.rooms.map((room) => ({ displayName: room.displayName, slug: room.slug }))"
            @dock-right="focusDockedChat"
            @select-room="emit('select-room', $event)"
          />
        </section>
      </aside>

      <main class="bg-conference-surface flex min-w-0 flex-1 flex-col overflow-hidden">
        <header
          data-testid="conference-layout-top-controls"
          class="border-conference-ink bg-conference-structure shrink-0 border-b px-1 py-1.5 text-white shadow-[0_1px_2px_rgba(0,0,0,0.15)] min-[1315px]:px-2"
        >
          <div class="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-stretch gap-1 min-[1315px]:gap-2">
            <div
              class="flex shrink-0 items-center gap-1 border-r border-black/30 pr-1 pl-2 min-[1315px]:gap-2 min-[1315px]:pr-2 min-[1315px]:pl-4"
            >
              <CIconButton
                size="toolbar"
                tone="neutral"
                variant="toolbar"
                :aria-pressed="isStageFullscreen"
                aria-label="Toggle stage fullscreen"
                title="Fullscreen"
                @click="toggleStageFullscreen"
              >
                <span aria-hidden="true" class="text-[18px] leading-none">⛶</span>
              </CIconButton>
              <CIconButton
                size="toolbar"
                tone="neutral"
                variant="toolbar"
                :aria-pressed="canToggleScreenshare ? isLocalScreenshareActive : undefined"
                aria-label="Toggle screenshare"
                title="Screenshare"
                :disabled="!canToggleScreenshare"
                @click="handleToggleScreenshare"
              >
                <IconLucidePresentation class="size-4.5" />
              </CIconButton>
              <CIconButton
                size="toolbar"
                tone="neutral"
                variant="toolbar"
                aria-label="Open announcement dialog"
                data-testid="open-one-time-events-dialog"
                title="Announcement"
                @click="openAnnouncementDialog"
              >
                <IconLucideInfo class="size-4.5" />
              </CIconButton>
              <CIconButton
                size="toolbar"
                tone="neutral"
                variant="toolbar"
                aria-label="Open conference event log"
                title="Event log"
                @click="showConferenceLogDialog = true"
              >
                <IconLucideHistory class="size-4.5" />
              </CIconButton>
            </div>

            <div class="flex min-w-0 items-center justify-center px-1">
              <div class="flex shrink-0 items-center gap-1 min-[1315px]:gap-2">
                <CMenu
                  v-model:open="showWhiteboardMenu"
                  align="left"
                  panel-test-id="conference-whiteboard-menu"
                  tone="chrome"
                  width="lg"
                >
                  <template #trigger="{ toggle }">
                    <CIconButton
                      size="toolbar"
                      tone="neutral"
                      variant="toolbar"
                      aria-label="Rename board"
                      data-testid="whiteboard-settings-trigger"
                      :disabled="!canOpenWhiteboardMenu"
                      title="Whiteboard settings"
                      @click="canOpenWhiteboardMenu ? toggle() : undefined"
                    >
                      <IconLucideSquarePen class="size-4.5" />
                    </CIconButton>
                  </template>
                  <template #default="{ close }">
                    <div class="conference-text-control w-62 space-y-3 px-3 py-3 text-white">
                      <label class="flex flex-col gap-1">
                        <span class="font-semibold tracking-[0.16em] text-white/70 uppercase">Board name</span>
                        <CInput
                          v-model="renameBoardDraft"
                          data-testid="whiteboard-board-input"
                          size="sm"
                          tone="light"
                          :disabled="!canRenameWhiteboardBoard"
                        />
                      </label>
                      <CButton
                        size="sm"
                        tone="accent"
                        data-testid="save-whiteboard-board"
                        :disabled="!canRenameWhiteboardBoard || !renameBoardDraft.trim()"
                        @click="void saveCurrentBoardName(close)"
                      >
                        Save
                      </CButton>

                      <div v-if="canCopyWhiteboardBoard" class="border-t border-white/15 pt-3">
                        <label class="flex flex-col gap-1">
                          <span class="font-semibold tracking-[0.16em] text-white/70 uppercase">Copy to breakout</span>
                          <CSelect
                            v-model="whiteboardCopyTargetRoomSlug"
                            data-testid="whiteboard-copy-target"
                            size="sm"
                            tone="light"
                          >
                            <option value="">Copy to breakout</option>
                            <option v-for="room in whiteboardCopyTargetRooms" :key="room.slug" :value="room.slug">
                              {{ room.displayName }}
                            </option>
                          </CSelect>
                        </label>
                        <CButton
                          class="mt-2"
                          size="sm"
                          tone="chrome"
                          data-testid="whiteboard-copy-board"
                          :disabled="!canCopyWhiteboardBoard || !whiteboardCopyTargetRoomSlug"
                          @click="void copyBoardToSelectedRoom(close)"
                        >
                          Copy board
                        </CButton>
                      </div>

                      <div
                        v-if="canResetWhiteboardBoard || canDeleteWhiteboardBoard"
                        class="flex items-center gap-2 border-t border-white/15 pt-3"
                      >
                        <CButton
                          v-if="canResetWhiteboardBoard"
                          size="sm"
                          tone="chrome"
                          data-testid="whiteboard-reset-board"
                          :disabled="!canResetWhiteboardBoard"
                          @click="void resetCurrentBoard(close)"
                        >
                          Reset
                        </CButton>
                        <CIconButton
                          v-if="canDeleteWhiteboardBoard"
                          size="toolbar"
                          tone="neutral"
                          variant="toolbar"
                          aria-label="Delete board"
                          data-testid="whiteboard-delete-board"
                          :disabled="!canDeleteWhiteboardBoard"
                          title="Delete board"
                          @click="void deleteBoard(close)"
                        >
                          <IconLucideTrash2 class="size-4.5" />
                        </CIconButton>
                      </div>
                    </div>
                  </template>
                </CMenu>

                <CSelect
                  v-model="selectedWhiteboardSlide"
                  data-testid="whiteboard-slide-select"
                  size="toolbar"
                  tone="dark"
                  width="toolbar"
                  :disabled="whiteboardFollowLocked"
                >
                  <option v-for="slide in whiteboardSlideOptions" :key="slide.value" :value="slide.value">
                    {{ slide.label }}
                  </option>
                </CSelect>
                <span data-testid="whiteboard-board-label" class="sr-only">{{ currentWhiteboardBoardName }}</span>
                <span data-testid="whiteboard-slide-value" class="sr-only">{{ currentWhiteboardSlideLabel }}</span>
                <span data-testid="whiteboard-follow-status" class="sr-only">{{ whiteboardFollowStatusLabel }}</span>
                <CIconButton
                  size="toolbar"
                  tone="neutral"
                  variant="toolbar"
                  aria-label="Previous slide"
                  data-testid="whiteboard-previous-slide"
                  title="Previous slide"
                  :disabled="!canGoToPreviousBoard"
                  @click="void goToPreviousBoard()"
                >
                  <IconLucideChevronLeft class="size-4.5" />
                </CIconButton>
                <CIconButton
                  size="toolbar"
                  tone="neutral"
                  variant="toolbar"
                  aria-label="Next slide"
                  data-testid="whiteboard-next-slide"
                  title="Next slide"
                  :disabled="!canGoToNextBoard"
                  @click="void goToNextBoard()"
                >
                  <IconLucideChevronRight class="size-4.5" />
                </CIconButton>
                <CIconButton
                  size="toolbar"
                  tone="neutral"
                  variant="toolbar"
                  aria-label="Add board"
                  data-testid="whiteboard-add-board"
                  :disabled="!canAddWhiteboardBoard || !whiteboardFrame"
                  title="Add board"
                  @click="void addBoard()"
                >
                  <IconLucidePlus class="size-4.5" />
                </CIconButton>
                <CIconButton
                  v-if="canDeleteWhiteboardBoard"
                  size="toolbar"
                  tone="neutral"
                  variant="toolbar"
                  aria-label="Delete board"
                  data-testid="whiteboard-delete-board-inline"
                  :disabled="!canDeleteWhiteboardBoard"
                  title="Delete board"
                  @click="void deleteBoard()"
                >
                  <IconLucideTrash2 class="size-4.5" />
                </CIconButton>
                <CIconButton
                  v-if="whiteboardCanToggleFollow"
                  size="toolbar"
                  tone="neutral"
                  variant="toolbar"
                  :aria-pressed="whiteboardFollowEnabled"
                  aria-label="Toggle follow mode"
                  data-testid="whiteboard-follow-toggle"
                  title="Toggle follow mode"
                  @click="void toggleBoardSync()"
                >
                  <IconLucideRefreshCcw class="size-4.5" />
                </CIconButton>
                <h2 data-testid="conference-current-room-title" class="sr-only">
                  {{ conference.currentRoom.displayName }}
                </h2>
              </div>
            </div>

            <div class="flex shrink-0 items-center gap-px border-l border-black/30 pl-1">
              <CMenu
                v-if="conference.viewer.role === 'teacher'"
                v-model:open="showTimerMenu"
                align="right"
                panel-test-id="shared-timer-menu"
                tone="chrome"
                width="md"
              >
                <template #trigger="{ toggle }">
                  <button
                    type="button"
                    data-testid="shared-timer-panel"
                    class="border-conference-panel-border bg-conference-structure conference-text-control hover:border-conference-accent-border hover:text-conference-control-highlight focus-visible:ring-conference-accent/25 inline-flex h-7 min-w-27 items-center justify-center gap-1.5 rounded-[1px] border px-3 font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition focus-visible:ring-2 focus-visible:outline-none"
                    @click="toggle"
                  >
                    <span aria-hidden="true" class="text-[11px] leading-none">◷</span>
                    <span data-testid="shared-timer-countdown">
                      {{ sharedTimerActive ? sharedTimerCountdownLabel.replace(' remaining', '') : '00:00:00' }}
                    </span>
                  </button>
                </template>
                <template #default>
                  <div class="conference-text-control w-44 px-2 py-2 text-white">
                    <p class="font-semibold tracking-[0.16em] text-slate-300 uppercase">
                      {{ sharedTimerActive ? 'Timer active' : 'Timer idle' }}
                    </p>
                    <p class="mt-1 text-white/80">{{ sharedTimerDetail }}</p>
                    <div class="mt-2 flex flex-col gap-1 border-t border-white/15 pt-2">
                      <CButton
                        v-for="preset in TIMER_PRESET_OPTIONS"
                        :key="preset.seconds"
                        size="sm"
                        tone="chrome"
                        :data-testid="`start-timer-${preset.seconds}`"
                        @click="void setSharedTimer(preset.seconds)"
                      >
                        {{ preset.label }}
                      </CButton>
                      <div class="my-1 border-t border-white/10"></div>
                      <CButton size="sm" tone="chrome" data-testid="open-custom-timer-dialog" @click="openCustomTimerDialog">
                        Custom...
                      </CButton>
                      <CButton
                        v-for="(savedValue, index) in savedCustomTimerValues"
                        :key="savedValue"
                        size="sm"
                        tone="chrome"
                        :data-testid="index === 0 ? 'reuse-custom-timer' : undefined"
                        @click="void applyStoredCustomTimer(savedValue)"
                      >
                        {{ savedValue }}
                      </CButton>
                      <CButton
                        v-if="sharedTimerActive"
                        size="sm"
                        tone="chrome"
                        data-testid="clear-timer"
                        @click="void setSharedTimer(0)"
                      >
                        Clear
                      </CButton>
                    </div>
                  </div>
                </template>
              </CMenu>
              <span
                v-else
                data-testid="shared-timer-panel"
                class="border-conference-panel-border bg-conference-structure conference-text-control inline-flex h-7 min-w-27 items-center justify-center gap-1.5 rounded-[1px] border px-3 font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
              >
                <span aria-hidden="true" class="text-[11px] leading-none">◷</span>
                <span data-testid="shared-timer-countdown">
                  {{ sharedTimerActive ? sharedTimerCountdownLabel.replace(' remaining', '') : '00:00:00' }}
                </span>
              </span>
              <CButton
                v-if="conference.viewer.role === 'teacher' || effectiveRecordingState !== 'off'"
                class="h-7 min-w-8 px-2 min-[1315px]:min-w-22 min-[1315px]:px-3"
                size="sm"
                :tone="recordingButtonTone"
                data-testid="recording-toolbar-toggle"
                :aria-label="recordingButtonLabel"
                :title="recordingButtonLabel"
                :disabled="recordingButtonDisabled"
                @click="void handleRecordingButtonClick()"
              >
                <IconLucideLoaderCircle
                  v-if="effectiveRecordingState === 'pending'"
                  aria-hidden="true"
                  class="size-3.5 animate-spin min-[1315px]:hidden"
                />
                <IconLucideSquare
                  v-else-if="effectiveRecordingState === 'on'"
                  aria-hidden="true"
                  class="size-3.5 min-[1315px]:hidden"
                />
                <IconLucideCircleDot v-else aria-hidden="true" class="size-3.5 min-[1315px]:hidden" />
                <span class="hidden min-[1315px]:inline">{{ recordingButtonLabel }}</span>
              </CButton>
              <CButton
                class="h-7 min-w-8 px-2 min-[1315px]:min-w-22 min-[1315px]:px-3"
                size="sm"
                :tone="videoPanelVisible ? 'accent' : 'chrome'"
                data-testid="video-panel-toggle"
                :aria-label="videoPanelButtonLabel"
                :title="videoPanelButtonLabel"
                :aria-pressed="videoPanelVisible"
                :disabled="videoPanelButtonDisabled"
                @click="toggleVideoPanel"
              >
                <IconLucideVideo aria-hidden="true" class="size-3.5 min-[1315px]:hidden" />
                <span class="hidden min-[1315px]:inline">{{ videoPanelButtonLabel }}</span>
                <template #trailing>
                  <span
                    v-if="showVideoPanelBadge"
                    aria-hidden="true"
                    data-testid="video-panel-toggle-badge"
                    class="text-conference-structure ml-1 inline-flex min-w-4.5 items-center justify-center rounded-full border border-white/25 bg-white px-1 text-[10px] leading-4 font-bold"
                  >
                    {{ activeCameraParticipantCount }}
                  </span>
                </template>
              </CButton>

              <CMenu
                v-if="roomSwitchMenuRooms.length > 1"
                v-model:open="showRoomSwitchMenu"
                align="right"
                panel-test-id="conference-room-switch-menu"
                tone="chrome"
                width="md"
              >
                <template #trigger="{ open, toggle }">
                  <CIconButton
                    class="h-7 w-10.5"
                    size="session-menu"
                    :tone="open ? 'gold' : 'neutral'"
                    data-testid="conference-room-switch-trigger"
                    aria-haspopup="menu"
                    :aria-expanded="open"
                    aria-label="Switch rooms"
                    :disabled="!canNavigateRooms"
                    :title="canNavigateRooms ? 'Switch rooms' : 'Room switching disabled'"
                    @click="toggle"
                  >
                    <span aria-hidden="true" class="text-[13px] leading-none">⇄</span>
                  </CIconButton>
                </template>
                <template #default="{ close }">
                  <div class="conference-text-control w-44 px-2 py-2 text-white">
                    <p class="font-semibold tracking-[0.16em] text-slate-300 uppercase">Change room</p>
                    <div class="mt-2 flex flex-col gap-1 border-t border-white/15 pt-2">
                      <CMenuItem
                        v-for="room in roomSwitchMenuRooms"
                        :key="room.slug"
                        :active="room.slug === conference.currentRoom.slug"
                        @click="selectRoomFromMenu(room.slug, close)"
                      >
                        {{ room.displayName }}
                      </CMenuItem>
                    </div>
                  </div>
                </template>
              </CMenu>

              <CMenu
                v-model:open="showSessionMenu"
                align="right"
                panel-test-id="conference-session-menu"
                tone="chrome"
                width="panel"
              >
                <template #trigger="{ open, toggle }">
                  <CButton
                    class="h-7 min-w-8 px-2 min-[1315px]:min-w-22 min-[1315px]:px-3"
                    size="sm"
                    :tone="open ? 'accent' : 'chrome'"
                    data-testid="conference-session-menu-trigger"
                    aria-haspopup="menu"
                    :aria-expanded="open"
                    aria-label="Session menu"
                    title="Session menu"
                    @click="toggle"
                  >
                    <IconLucideMenu aria-hidden="true" class="size-3.5" />
                    <span class="hidden min-[1315px]:inline">Menu</span>
                  </CButton>
                </template>
                <template #default="{ close }">
                  <div class="conference-text-control w-46.5 text-white">
                    <div v-if="conference.viewer.role === 'teacher'" class="border-b border-white/15 py-1">
                      <CMenuItem
                        :disabled="killSessionButtonDisabled"
                        data-testid="conference-kill-session-button"
                        @click="openKillSessionDialog(close)"
                      >
                        {{ endSessionPending ? 'Ending Session…' : 'Kill Session' }}
                      </CMenuItem>
                    </div>

                    <div class="px-2.5 py-1">
                      <p class="conference-text-meta pb-1 font-semibold tracking-[0.2em] text-white/70 uppercase">
                        Session Keyboard Shortcuts
                      </p>
                      <ul class="space-y-0.75 leading-4 text-white/90">
                        <li>CTRL + 1: Green Check</li>
                        <li>CTRL + 2: Red X</li>
                        <li>CTRL + 3: Confused Face</li>
                        <li>CTRL + 4: Raise Hand</li>
                      </ul>
                    </div>

                    <div class="border-t border-white/15 px-2.5 py-1">
                      <p class="conference-text-meta pb-1 font-semibold tracking-[0.2em] text-white/70 uppercase">
                        Whiteboard Keyboard Shortcuts
                      </p>
                      <ul class="space-y-0.75 leading-4 text-white/90">
                        <li>CTRL + A: Select All</li>
                        <li>CTRL + C: Copy</li>
                        <li>CTRL + V: Paste</li>
                        <li>CTRL + G: Group</li>
                        <li>CTRL + U: Ungroup</li>
                        <li>CTRL + Z: Undo</li>
                        <li>CTRL + Y: Redo</li>
                        <li>CTRL + L: Lock</li>
                        <li>CTRL + Spacebar: Tool Change</li>
                      </ul>
                    </div>

                    <div class="border-t border-white/15 py-1">
                      <CMenuItem @click="openPreferencesDialog(close)">Set Preferences</CMenuItem>
                    </div>
                  </div>
                </template>
              </CMenu>
            </div>
          </div>
        </header>

        <section
          ref="stageRegion"
          data-testid="conference-layout-stage"
          tabindex="-1"
          class="flex min-h-0 min-w-0 flex-1 bg-white"
        >
          <div class="flex min-h-0 min-w-0 flex-1 flex-col bg-white">
            <div class="min-h-0 flex-1 overflow-hidden bg-white">
              <div v-if="featuredVideoTrack" class="flex h-full min-h-0 bg-slate-950">
                <MediaTrackAttachment :track="featuredVideoTrack" class="h-full w-full object-contain" />
              </div>

              <div v-else data-testid="whiteboard-surface" class="h-full w-full bg-white">
                <iframe
                  ref="whiteboardFrame"
                  :key="whiteboardFrameSrc"
                  data-testid="conference-stage-whiteboard"
                  :src="whiteboardFrameSrc"
                  class="h-full w-full border-0 bg-white"
                  allow="clipboard-read; clipboard-write"
                  @load="handleWhiteboardLoad"
                />
              </div>
              <div class="hidden">
                <MediaTrackAttachment v-for="track in audioTracks" :key="track.id" :track="track" />
              </div>
            </div>
          </div>

          <aside
            v-if="dockAreaVisible"
            data-testid="conference-layout-dock-panel"
            class="border-conference-dock-border bg-conference-dock flex h-full w-66.5 min-w-0 shrink-0 border-l"
          >
            <div class="flex min-h-0 min-w-0 flex-1 flex-col">
              <div
                v-if="showDockTabs"
                class="border-conference-dock-border bg-conference-dock-surface flex shrink-0 gap-1 border-b px-2 py-2"
              >
                <button
                  v-for="panel in dockedPanels"
                  :key="panel.id"
                  type="button"
                  class="conference-text-control inline-flex flex-1 items-center justify-center rounded-[3px] border px-2 py-1 font-semibold tracking-[0.18em] uppercase transition focus-visible:ring-2 focus-visible:ring-sky-200 focus-visible:outline-none"
                  :class="
                    activeDockPanel === panel.id
                      ? 'border-conference-dock-accent text-conference-dock-text bg-white shadow-[0_1px_2px_rgba(15,23,42,0.08)]'
                      : 'text-conference-dock-text hover:border-conference-dock-border hover:text-conference-dock-text border-transparent bg-transparent hover:bg-white/70'
                  "
                  :data-testid="`conference-dock-tab-${panel.id}`"
                  @click="activeDockPanel = panel.id"
                >
                  {{ panel.label }}
                </button>
              </div>

              <section
                v-show="showDockedChatPanel"
                id="conference-dock-chat"
                class="bg-conference-dock flex min-h-0 min-w-0 flex-1"
              >
                <ChatPanel
                  v-if="!showChatBlackout"
                  docked
                  dockable
                  :chat-state="chatState"
                  :conference-slug="conference.conference.slug"
                  :requested-thread-key="requestedChatThreadKey"
                  :current-room-slug="conference.currentRoom.slug"
                  :rooms="conference.rooms.map((room) => ({ displayName: room.displayName, slug: room.slug }))"
                  @dock-left="undockChat"
                  @select-room="emit('select-room', $event)"
                  class="min-w-0"
                />
                <div v-else aria-hidden="true" class="min-h-0 flex-1 bg-black" data-testid="chat-disabled-blackout" />
              </section>

              <section v-show="showDockedVideoPanel" id="conference-dock-video" class="bg-conference-dock flex min-h-0 flex-1">
                <ConferenceVideoPanel
                  :active-camera-participant-count="activeCameraParticipantCount"
                  :current-room-name="conference.currentRoom.displayName"
                  :screen-share-track="screenShareTrack"
                  :self-video-enabled="selfVideoEnabled"
                  :video-panel-layout="videoPanelLayout"
                  :video-panel-visible="videoPanelVisible"
                  :video-tracks="videoTracks"
                  @close="hideVideoPanelManually"
                  @toggle-layout="toggleVideoPanelLayout"
                />
              </section>
            </div>
          </aside>
        </section>
      </main>
    </div>

    <ConferenceVideoPanel
      v-if="videoPanelVisible && videoPanelLayout === 'floating'"
      :active-camera-participant-count="activeCameraParticipantCount"
      :current-room-name="conference.currentRoom.displayName"
      :screen-share-track="screenShareTrack"
      :self-video-enabled="selfVideoEnabled"
      :video-panel-layout="videoPanelLayout"
      :video-panel-visible="videoPanelVisible"
      :video-tracks="videoTracks"
      @close="hideVideoPanelManually"
      @toggle-layout="toggleVideoPanelLayout"
    />
  </div>

  <CDialog
    v-model:open="showKillSessionDialog"
    panel-test-id="conference-kill-session-dialog"
    title="Kill session"
    description="Everyone currently in the conference will be sent to the end-of-session screen. Breakout rooms will close and the whiteboard will reset."
    width="md"
  >
    <template #default>
      <div class="space-y-3">
        <p class="conference-text-body text-slate-700">End the current session for everyone who is connected right now?</p>
        <p v-if="killSessionWillStopRecording" class="conference-text-caption text-slate-600">
          Recording is active and will be stopped before the session is ended.
        </p>
      </div>
    </template>
    <template #footer="{ close }">
      <div class="flex justify-end gap-1.5">
        <CButton size="sm" tone="chrome" :disabled="endSessionPending" @click="close">Cancel</CButton>
        <CButton
          size="sm"
          tone="danger"
          data-testid="conference-confirm-kill-session-button"
          :disabled="killSessionButtonDisabled"
          @click="void confirmKillSession(close)"
        >
          {{ endSessionPending ? 'Ending Session…' : 'Kill Session' }}
        </CButton>
      </div>
    </template>
  </CDialog>

  <CDialog
    v-model:open="showCustomTimerDialog"
    panel-test-id="custom-timer-dialog"
    title="Custom timer"
    description="Enter seconds, mm:ss, or hh:mm:ss. Recent custom values are saved for reuse."
    width="md"
  >
    <template #default>
      <div class="flex flex-col gap-3 p-2">
        <label class="flex flex-col gap-1">
          <span class="conference-text-caption font-semibold tracking-[0.16em] text-slate-500 uppercase">Time</span>
          <CInput v-model="customTimerDraft" data-autofocus data-testid="custom-timer-input" size="sm" placeholder="5:00" />
        </label>
        <p class="conference-text-caption text-slate-600">
          Examples: `5` for 5 seconds, `5:5` for 5 minutes 5 seconds, `5:5:5` for 5 hours 5 minutes 5 seconds.
        </p>
        <p v-if="customTimerError" class="conference-text-caption text-rose-700" role="alert">{{ customTimerError }}</p>
      </div>
    </template>
    <template #footer="{ close }">
      <div class="flex justify-end gap-1.5">
        <CButton size="sm" tone="chrome" @click="close">Cancel</CButton>
        <CButton size="sm" tone="accent" data-testid="apply-custom-timer" @click="void submitCustomTimer(close)">
          Set timer
        </CButton>
      </div>
    </template>
  </CDialog>

  <CDialog
    v-model:open="showAnnouncementDialog"
    panel-test-id="conference-announcement-dialog"
    title="Announcement"
    description="Write a message to broadcast to everyone currently in the conference."
    width="md"
  >
    <template #default>
      <div class="space-y-3">
        <div
          v-if="oneTimeEventError"
          class="conference-text-caption rounded-[1px] border border-rose-200 bg-rose-50 px-2.5 py-2 text-rose-700"
        >
          {{ oneTimeEventError }}
        </div>
        <label class="flex flex-col gap-1">
          <span class="conference-text-caption font-semibold tracking-[0.16em] text-slate-500 uppercase">Message</span>
          <textarea
            v-model="announcementDraft"
            data-autofocus
            data-testid="announcement-input"
            rows="6"
            class="border-conference-line-soft conference-text-body min-h-32 w-full resize-y rounded-[1px] border bg-white px-2.5 py-2 text-slate-950 transition outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="oneTimeEventPending || conference.viewer.isKicked"
            placeholder="Class, please wrap up your breakout discussion and head back to the main room."
          />
        </label>
        <p class="conference-text-caption text-slate-600">Line breaks are preserved when the announcement is shown.</p>
      </div>
    </template>
    <template #footer="{ close }">
      <div class="flex justify-end gap-1.5">
        <CButton size="sm" tone="chrome" @click="close">Cancel</CButton>
        <CButton
          size="sm"
          tone="accent"
          data-testid="publish-announcement"
          :disabled="oneTimeEventPending || conference.viewer.isKicked || !announcementDraft.trim()"
          @click="void submitAnnouncement(close)"
        >
          Send announcement
        </CButton>
      </div>
    </template>
  </CDialog>

  <CDialog
    v-model:open="showConferenceLogDialog"
    panel-test-id="conference-log-dialog"
    title="Recent activity"
    description="See recent announcements, room actions, and classroom updates."
    width="lg"
  >
    <p
      v-if="conferenceLogEntries.length === 0"
      class="border-conference-line-soft bg-conference-surface-muted conference-text-body rounded-[1px] border border-dashed px-3 py-4 text-slate-600"
    >
      No events have happened yet.
    </p>
    <ul v-else class="space-y-2">
      <li
        v-for="entry in conferenceLogEntries"
        :key="entry.id"
        class="border-conference-line-soft bg-conference-surface-muted rounded-[1px] border px-2.5 py-2"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="text-[11px] font-semibold text-slate-950">{{ entry.label }}</p>
            <p v-if="entry.detail" class="conference-text-caption mt-0.5 text-slate-600">{{ entry.detail }}</p>
          </div>
          <span class="conference-text-caption shrink-0 font-semibold tracking-[0.18em] text-slate-400 uppercase">
            {{ formatLogTime(entry.createdAt) }}
          </span>
        </div>
      </li>
    </ul>
  </CDialog>

  <CDialog
    v-model:open="showRecordingErrorDialog"
    panel-test-id="recording-error-dialog"
    title="Recording error"
    description="The recording request could not be completed."
    width="md"
  >
    <template #default>
      <div class="space-y-3">
        <p class="conference-text-caption rounded-[1px] border border-rose-200 bg-rose-50 px-2.5 py-2 text-rose-700" role="alert">
          {{ recordingErrorDialogMessage }}
        </p>
      </div>
    </template>
  </CDialog>

  <CDialog
    v-model:open="showPreferencesDialog"
    content-padding="none"
    :content-scrollable="false"
    hide-footer
    hide-header
    panel-test-id="conference-preferences-dialog"
    width="xl"
  >
    <template #default="{ close }">
      <MediaPreferencesPanel
        :audio-input-options="audioInputOptions"
        :audio-output-options="audioOutputOptions"
        :camera-options="cameraOptions"
        :notification-preference-definitions="notificationPreferenceDefinitions"
        :notification-preferences="notificationPreferences"
        :refresh-device-options="refreshDeviceOptions"
        :selected-audio-input="selectedAudioInput"
        :selected-audio-output="selectedAudioOutput"
        :selected-camera="selectedCamera"
        :selected-camera-label="selectedCameraLabel"
        :selected-text-size="selectedTextSize"
        @close="() => close()"
        @update:selected-audio-input="setSelectedAudioInput"
        @update:selected-audio-output="setSelectedAudioOutput"
        @update:selected-camera="setSelectedCamera"
        @update:selected-text-size="setSelectedTextSize"
        @update:notification-preference="setNotificationPreference($event.key, $event.value)"
      />
    </template>
  </CDialog>

  <ConferenceContextMenu
    :chat-enabled="conference.conference.chatEnabled"
    :conference-slug="conference.conference.slug"
    :menu-state="contextMenuState"
    :rooms="conference.rooms"
    :viewer-id="conference.viewer.id"
    :viewer-role="conference.viewer.role"
    @close="closeContextMenu"
    @notice="appendConferenceLog($event.label, $event.detail)"
    @open-thread="openDirectThread"
  />

  <CDialog
    v-model:open="activeAnnouncementDialogOpen"
    panel-test-id="conference-active-announcement-dialog"
    title="Announcement"
    description="A teacher sent a conference-wide announcement."
    width="md"
    :close-on-backdrop="false"
    :close-on-escape="false"
  >
    <template #default>
      <div class="space-y-3">
        <p class="conference-text-caption font-semibold tracking-[0.16em] text-slate-500 uppercase">Message</p>
        <div class="rounded-[1px] border border-sky-200 bg-sky-50 px-3 py-3">
          <p class="conference-text-body whitespace-pre-wrap text-slate-950">{{ announcementMessage }}</p>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end">
        <CButton size="sm" tone="accent" @click="dismissAnnouncement">OK</CButton>
      </div>
    </template>
  </CDialog>

  <OneTimeEventOverlay
    :active-sound-name="activeSoundName"
    :can-manage-active-sound="conference.viewer.role === 'teacher'"
    :fireworks-text="fireworksText"
    :random-selection="randomSelection"
    @stop-sound="void handleStopRoomSound()"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue';
import { useConvexClient, useConvexMutation, useConvexQuery } from 'convex-vue';
import IconLucideChevronLeft from '~icons/lucide/chevron-left';
import IconLucideChevronRight from '~icons/lucide/chevron-right';
import IconLucideCircleDot from '~icons/lucide/circle-dot';
import IconLucideHistory from '~icons/lucide/history';
import IconLucideInfo from '~icons/lucide/info';
import IconLucideLoaderCircle from '~icons/lucide/loader-circle';
import IconLucideMenu from '~icons/lucide/menu';
import IconLucidePlus from '~icons/lucide/plus';
import IconLucidePresentation from '~icons/lucide/presentation';
import IconLucideRefreshCcw from '~icons/lucide/refresh-ccw';
import IconLucideSquare from '~icons/lucide/square';
import IconLucideSquarePen from '~icons/lucide/square-pen';
import IconLucideTrash2 from '~icons/lucide/trash-2';
import IconLucideVideo from '~icons/lucide/video';
import { api } from '../../convex/_generated/api';
import type { ConferenceLogEntry, ConferenceState } from '../convex/useConference';
import { readStoredToken } from '../convex/auth';
import {
  eventCueSources,
  parsePendingDispatchEvent,
  soundEffectSources,
  type PendingDispatchEventRecord,
} from '../features/oneTimeEvents';
import {
  defaultAccessibilityPreferences,
  defaultNotificationPreferences,
  type AccessibilityTextSize,
  type NotificationPreferenceKey,
  notificationCueSources,
  notificationPreferenceDefinitions,
} from '../features/preferences';
import { buildJitsiRoomName, buildWhiteboardUrl, SHARED_TIMER_CUSTOM_HISTORY_LIMIT } from '../jitsi/config';
import { useConferenceMediaRuntime } from '../jitsi/runtime';
import ChatPanel from './ChatPanel.vue';
import ConferenceContextMenu from './ConferenceContextMenu.vue';
import ConferenceRoomSection from './ConferenceRoomSection.vue';
import ConferenceSignalBar from './ConferenceSignalBar.vue';
import ConferenceTeacherControlsBar from './ConferenceTeacherControlsBar.vue';
import OneTimeEventOverlay from './OneTimeEventOverlay.vue';
import ConferenceVideoPanel from './ConferenceVideoPanel.vue';
import MediaControlBar from './MediaControlBar.vue';
import MediaPreferencesPanel from './MediaPreferencesPanel.vue';
import MediaTrackAttachment from './MediaTrackAttachment.vue';
import CButton from './primitives/CButton.vue';
import CDialog from './primitives/CDialog.vue';
import CIconButton from './primitives/CIconButton.vue';
import CInput from './primitives/CInput.vue';
import CMenu from './primitives/CMenu.vue';
import CMenuItem from './primitives/CMenuItem.vue';
import CSelect from './primitives/CSelect.vue';

type RoomContextMenuStateRoom = ConferenceState['rooms'][number] & {
  participants: ConferenceState['participants'];
};

const props = defineProps<{
  error?: string | null;
  conference: ConferenceState;
  switchingRoom: boolean;
}>();

type PendingDispatchEventQueryRecord = PendingDispatchEventRecord & {
  createdAt: number;
  id: string;
};

type WhiteboardUserPayload = {
  id: string;
  moderator: boolean;
  name: string;
};

const emit = defineEmits<{
  (event: 'select-room', roomSlug: string): void;
}>();

const ROOM_ROSTER_HEIGHT_STORAGE_KEY = 'conference.room-roster-height';
const CUSTOM_TIMER_STORAGE_KEY = 'conference.shared-timer.custom-value';
const TIMER_PRESET_OPTIONS = [
  { label: '1:00', seconds: 60 },
  { label: '5:00', seconds: 300 },
  { label: '10:00', seconds: 600 },
  { label: '15:00', seconds: 900 },
] as const;
const DEFAULT_ROOM_ROSTER_HEIGHT = 425;
const MIN_ROOM_ROSTER_HEIGHT = 240;
const MAX_ROOM_ROSTER_HEIGHT = 640;
const WHITEBOARD_CONNECT_RETRY_MS = 500;
const WHITEBOARD_CONNECT_TIMEOUT_MS = 10_000;
const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

const canNavigateRooms = computed(
  () => props.conference.viewer.role === 'teacher' || props.conference.viewer.permissions.navigate,
);
const canUseChatPanel = computed(() => props.conference.viewer.role === 'teacher' || props.conference.conference.chatEnabled);
const showChatBlackout = computed(() => props.conference.viewer.role === 'student' && !props.conference.conference.chatEnabled);
const canViewChatPanel = computed(() => canUseChatPanel.value || showChatBlackout.value);
const viewerIsTeacher = computed(() => props.conference.viewer.role === 'teacher');
const currentConferenceRoom = computed(
  () => props.conference.rooms.find((room) => room.slug === props.conference.currentRoom.slug) ?? null,
);
const canAdministerWhiteboard = computed(() => viewerIsTeacher.value || props.conference.viewer.permissions.topMenuControl);
const stageRegion = ref<HTMLElement | null>(null);
const whiteboardFrame = ref<HTMLIFrameElement | null>(null);
const whiteboardSlideNames = ref<string[]>([]);
const isStageFullscreen = ref(false);
const isWhiteboardReady = ref(false);
const queuedWhiteboardMethods: Array<{ args: unknown[]; method: string }> = [];
const pendingWhiteboardSlide = ref<number | null>(null);
const whiteboardRuntimeSlide = ref<number | null>(null);
let whiteboardConnectRetryHandle: number | null = null;
let whiteboardConnectTimeoutHandle: number | null = null;
let whiteboardConnectionWarningShown = false;
const roomRosterHeight = ref(readRoomRosterHeight());
const isChatDocked = ref(false);
const activeDockPanel = ref<'chat' | 'video'>('chat');
const videoPanelManuallyHiddenForActiveCameraSession = ref(false);
const showPreferencesDialog = ref(false);
const showAnnouncementDialog = ref(false);
const showCustomTimerDialog = ref(false);
const showRoomSwitchMenu = ref(false);
const showTimerMenu = ref(false);
const showSessionMenu = ref(false);
const showConferenceLogDialog = ref(false);
const showKillSessionDialog = ref(false);
const showRecordingErrorDialog = ref(false);
const showWhiteboardMenu = ref(false);
const endSessionPending = ref(false);
const savedCustomTimerValues = ref(readCustomTimerStorageValues());
const customTimerDraft = ref(savedCustomTimerValues.value[0] ?? '00:05:00');
const customTimerError = ref<string | null>(null);
const recordingErrorDialogMessage = ref<string | null>(null);
const showPendingRecordingError = ref(false);
const renameBoardDraft = ref('');
const whiteboardCopyTargetRoomSlug = ref('');
const localConferenceLogEntries = ref<ConferenceLogEntry[]>([]);
const conferenceEventLog = ref<ConferenceLogEntry[]>([]);
const conferenceLogEntries = computed(() => {
  return [...conferenceEventLog.value, ...localConferenceLogEntries.value].sort(
    (left, right) => right.createdAt - left.createdAt,
  );
});
let nextConferenceLogEntryId = 0;
const selectedParticipantIds = ref<ConferenceState['participants'][number]['id'][]>([]);
const randomSelectionParticipantId = ref<ConferenceState['participants'][number]['id'] | null>(null);
const dragState = ref<{
  participantIds: ConferenceState['participants'][number]['id'][];
  sourceRoomSlugs: string[];
} | null>(null);
let viewerDrawingState = false;
const requestedChatThreadKey = ref<string | null>(null);
const contextMenuState = ref<
  | {
      kind: 'participant';
      participant: ConferenceState['participants'][number];
      participants: ConferenceState['participants'];
      x: number;
      y: number;
    }
  | {
      kind: 'room';
      room: RoomContextMenuStateRoom;
      x: number;
      y: number;
    }
  | null
>(null);
const dockedVideoVisible = computed(() => videoPanelVisible.value && videoPanelLayout.value === 'docked');
const highlightedParticipantIds = computed(() =>
  randomSelectionParticipantId.value === null ? [] : [randomSelectionParticipantId.value],
);
const confettiHighlightedParticipantIds = computed(() =>
  confettiHighlightedParticipantId.value === null ? [] : [confettiHighlightedParticipantId.value],
);
const participantsByRoomSlug = computed(() => {
  const nextMap = new Map<string, ConferenceState['participants']>();

  for (const participant of props.conference.participants) {
    const roomParticipants = nextMap.get(participant.apparentRoom.slug);

    if (roomParticipants) {
      roomParticipants.push(participant);
    } else {
      nextMap.set(participant.apparentRoom.slug, [participant]);
    }
  }

  return nextMap;
});
const dockedPanels = computed(() => {
  const panels: { id: 'chat' | 'video'; label: string }[] = [];

  if (isChatDocked.value && canViewChatPanel.value) {
    panels.push({ id: 'chat', label: 'Chat' });
  }

  if (dockedVideoVisible.value) {
    panels.push({ id: 'video', label: 'Video' });
  }

  return panels;
});
const dockAreaVisible = computed(() => dockedPanels.value.length > 0);
const showDockTabs = computed(() => dockedPanels.value.length > 1);
const showDockedChatPanel = computed(() => isChatDocked.value && canViewChatPanel.value && activeDockPanel.value === 'chat');
const showDockedVideoPanel = computed(() => dockedVideoVisible.value && activeDockPanel.value === 'video');
const showRailChatPanel = computed(() => canViewChatPanel.value && !isChatDocked.value);
const roomRosterStyle = computed(() =>
  showRailChatPanel.value
    ? {
        minHeight: `${roomRosterHeight.value}px`,
        maxHeight: `${roomRosterHeight.value}px`,
      }
    : {
        minHeight: `${roomRosterHeight.value}px`,
      },
);
const draggedParticipantIds = computed(() => dragState.value?.participantIds ?? []);
const dragSourceRoomSlugs = computed(() => dragState.value?.sourceRoomSlugs ?? []);
const canToggleScreenshare = computed(
  () => props.conference.viewer.role === 'teacher' || props.conference.viewer.permissions.screenshare,
);
const viewerActiveTrackCount = computed(() => {
  let count = 0;

  if (props.conference.viewer.trackState.audio) {
    count += 1;
  }

  if (props.conference.viewer.trackState.camera) {
    count += 1;
  }

  if (props.conference.viewer.trackState.screenshare) {
    count += 1;
  }

  return count;
});
const hasBreakoutRooms = computed(() => props.conference.rooms.some((room) => room.kind === 'breakout'));
const hasOpenBreakoutRooms = computed(() => props.conference.rooms.some((room) => room.kind === 'breakout' && room.isOpen));
const roomSwitchingEnabled = computed(() => props.conference.conference.permissions.navigate);
const canToggleRoomSwitching = computed(() => props.conference.viewer.role === 'teacher' && !isSettingGlobalPermission.value);
const canCloseBreakoutRooms = computed(
  () => props.conference.viewer.role === 'teacher' && hasOpenBreakoutRooms.value && !isClosingBreakoutRooms.value,
);
const roomSwitchMenuRooms = computed(() => props.conference.rooms.filter((room) => room.isOpen || room.isActive));
const showViewerCloakMarker = computed(
  () => props.conference.viewer.role === 'teacher' && Boolean(props.conference.viewer.cloakedRoomName),
);
let cleanupRoomRosterResize: (() => void) | null = null;
let notificationPersistHandle: number | null = null;
let lastPersistedPreferencesJson = '';
let hasHydratedPreferences = false;
let activeNotificationAudio: HTMLAudioElement | null = null;
let activeSoundEffectAudio: HTMLAudioElement | null = null;
let activeConfettiAudio: HTMLAudioElement | null = null;
let confettiOverlayHandle: number | null = null;
let confettiCanvasHandle: number | null = null;
let fireworksOverlayHandle: number | null = null;
let randomSelectionOverlayHandle: number | null = null;
const processingDispatchEventIds = new Set<string>();
const announcementMessage = ref<string | null>(null);
const announcementDraft = ref('');
const activeAnnouncementDialogOpen = computed({
  get: () => announcementMessage.value !== null,
  set: (isOpen: boolean) => {
    if (!isOpen) {
      dismissAnnouncement();
    }
  },
});
const confettiHighlightedParticipantId = ref<ConferenceState['participants'][number]['id'] | null>(null);
const fireworksText = ref<string | null>(null);
const randomSelection = ref<{ roomName: string; selectedParticipantName: string } | null>(null);
const activeSoundName = ref<string | null>(null);
const oneTimeEventError = ref<string | null>(null);
const oneTimeEventPending = ref(false);
const timerNow = ref(Date.now());

const queryArgs = computed(() => ({
  conferenceSlug: props.conference.conference.slug,
}));
const currentWhiteboardQueryArgs = computed(() => ({
  conferenceSlug: props.conference.conference.slug,
  roomSlug: props.conference.currentRoom.slug,
}));
const convexClient = useConvexClient();
const { mutate: publishAnnouncement } = useConvexMutation(api.announcements.publishAnnouncement);
const { data: chatState } = useConvexQuery(api.chat.getState, queryArgs);
const { data: eventLogState } = useConvexQuery(api.conferences.getEventLog, queryArgs);
const { data: pendingDispatchEvents } = useConvexQuery(api.events.getPending, queryArgs);
const { data: whiteboardState } = useConvexQuery(api.whiteboards.getState, currentWhiteboardQueryArgs);
const { mutate: closeBreakoutRoom } = useConvexMutation(api.rooms.closeBreakoutRoom);
const { mutate: closeBreakoutRooms, isPending: isClosingBreakoutRooms } = useConvexMutation(api.rooms.closeBreakoutRooms);
const { mutate: consumeDispatchEvent } = useConvexMutation(api.events.consume);
const { mutate: adjustParticipationCount } = useConvexMutation(api.participants.adjustParticipationCount);
const { mutate: moveParticipantsToRoom } = useConvexMutation(api.participants.moveParticipantsToRoom);
const { mutate: setTimer } = useConvexMutation(api.conferences.setTimer);
const { mutate: setGlobalPermission, isPending: isSettingGlobalPermission } = useConvexMutation(
  api.permissions.setGlobalPermission,
);
const { mutate: setCurrentBoardState } = useConvexMutation(api.whiteboards.setCurrentBoardState);
const { mutate: setCurrentSlide } = useConvexMutation(api.whiteboards.setCurrentSlide);
const { mutate: setDrawingState } = useConvexMutation(api.presence.setDrawingState);
const { mutate: setPreferences } = useConvexMutation(api.participants.setPreferences);
const { mutate: setWhiteboardFollowMode } = useConvexMutation(api.whiteboards.setFollowMode);
const { mutate: stopSoundEffect } = useConvexMutation(api.sounds.stopSoundEffect);
const notificationPreferences = ref({ ...defaultNotificationPreferences });
const selectedTextSize = ref<AccessibilityTextSize>(defaultAccessibilityPreferences.textSize);

watch(
  eventLogState,
  (nextEventLog) => {
    conferenceEventLog.value = (nextEventLog as ConferenceLogEntry[] | null | undefined) ?? [];
  },
  { immediate: true },
);

function syncConferenceTextSize(): void {
  document.body.dataset.conferenceTextSize = selectedTextSize.value;
}

const {
  audioInputOptions,
  audioOutputOptions,
  audioTracks,
  canToggleAudio,
  canToggleCamera,
  cameraOptions,
  isRecordingStatePending,
  refreshDeviceOptions,
  recordingCommandInFlight,
  recordingError,
  recordingState,
  runtimeError,
  runtimeStatus,
  localScreenShareTrack,
  screenShareTrack,
  speakingParticipantIds,
  selectedAudioInput,
  selectedAudioOutput,
  selectedCamera,
  selfVideoEnabled,
  selectedCameraLabel,
  setScreenshareActive,
  toggleRecording,
  updateTrackIntent,
  videoPanelLayout,
  videoPanelVisible,
  videoTracks,
} = useConferenceMediaRuntime(
  computed(() => props.conference.conference.slug),
  toRef(props, 'conference'),
);

const featuredVideoTrack = computed(() => screenShareTrack.value ?? null);
const isLocalScreenshareActive = computed(() => localScreenShareTrack.value !== null);
const whiteboardBoardId = computed(() => buildJitsiRoomName(props.conference.conference.slug, props.conference.currentRoom.slug));
const whiteboardUsers = computed<WhiteboardUserPayload[]>(() => {
  const usersById = new Map<string, WhiteboardUserPayload>();

  for (const participant of props.conference.participants) {
    usersById.set(String(participant.id), {
      id: String(participant.id),
      moderator: participant.role === 'teacher',
      name: participant.displayName,
    });
  }

  usersById.set(String(props.conference.viewer.id), {
    id: String(props.conference.viewer.id),
    moderator: props.conference.viewer.role === 'teacher',
    name: props.conference.viewer.displayName,
  });

  return [...usersById.values()];
});
const whiteboardLocalPermissions = computed(() => {
  const canEditWhiteboard = viewerIsTeacher.value || props.conference.viewer.permissions.whiteboard;

  return {
    canDraw: canEditWhiteboard,
    canNavigate: canEditWhiteboard,
    isOwner: viewerIsTeacher.value,
    moderator: viewerIsTeacher.value,
  };
});
const whiteboardFrameSrc = computed(() => {
  const cacheDate = new Date();
  const url = new URL(buildWhiteboardUrl(props.conference.conference.slug, props.conference.currentRoom.slug, readStoredToken()));

  url.searchParams.set('cache', `${cacheDate.getMonth() + 1}-${cacheDate.getDate()}-${cacheDate.getFullYear()}`);
  url.hash = 'navigation=false';

  return url.toString();
});
const sharedTimerRemainingMs = computed(() => {
  const expiration = props.conference.conference.timerExpirationDate ?? 0;

  if (expiration <= 0) {
    return 0;
  }

  return Math.max(0, expiration - timerNow.value);
});
const sharedTimerActive = computed(() => sharedTimerRemainingMs.value > 0);
const sharedTimerCountdownLabel = computed(() => {
  if (!sharedTimerActive.value) {
    return '0:00 remaining';
  }

  const totalSeconds = Math.ceil(sharedTimerRemainingMs.value / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')} remaining`;
});
const sharedTimerDetail = computed(() =>
  sharedTimerActive.value ? 'Classroom timer is running.' : 'No classroom timer is running.',
);
const effectiveRecordingState = computed(() => {
  if (recordingCommandInFlight.value) {
    return 'pending';
  }

  if (props.conference.viewer.role === 'teacher' && isRecordingStatePending.value && recordingState.value === null) {
    return 'pending';
  }

  return recordingState.value?.state ?? 'off';
});
const recordingButtonLabel = computed(() => {
  switch (effectiveRecordingState.value) {
    case 'on':
      return 'Recording';
    case 'pending':
      return 'Pending';
    case 'error':
      return props.conference.viewer.role === 'teacher' ? 'Retry recording' : 'Recording';
    default:
      return 'Record';
  }
});
const recordingButtonTone = computed(() => {
  switch (effectiveRecordingState.value) {
    case 'on':
      return 'accent';
    case 'pending':
      return 'panel';
    case 'error':
      return 'danger';
    default:
      return 'chrome';
  }
});
const recordingButtonDisabled = computed(
  () => props.conference.viewer.role !== 'teacher' || props.conference.viewer.isKicked || recordingCommandInFlight.value !== null,
);
const killSessionButtonDisabled = computed(
  () =>
    props.conference.viewer.role !== 'teacher' ||
    props.conference.viewer.isKicked ||
    endSessionPending.value ||
    effectiveRecordingState.value === 'pending',
);
const killSessionWillStopRecording = computed(
  () => effectiveRecordingState.value === 'on' || recordingState.value?.desiredState === 'on',
);
const currentWhiteboardBoardName = computed(
  () => whiteboardState.value?.currentBoard ?? `${props.conference.currentRoom.displayName} Board`,
);
const currentWhiteboardSlide = computed(() => whiteboardState.value?.currentSlide ?? 0);
const visibleWhiteboardSlide = computed(
  () => pendingWhiteboardSlide.value ?? whiteboardRuntimeSlide.value ?? currentWhiteboardSlide.value,
);
const whiteboardSlideCount = computed(() => Math.max(whiteboardSlideNames.value.length, visibleWhiteboardSlide.value + 1, 1));
const selectedWhiteboardSlide = computed({
  get: () => String(visibleWhiteboardSlide.value),
  set: (value: string | null) => {
    const nextSlide = value === null ? NaN : Number(value);

    if (
      !Number.isInteger(nextSlide) ||
      nextSlide < 0 ||
      nextSlide >= whiteboardSlideCount.value ||
      nextSlide === visibleWhiteboardSlide.value
    ) {
      return;
    }

    void updateWhiteboardSlide(nextSlide);
  },
});
const whiteboardSlideOptions = computed(() => {
  return Array.from({ length: whiteboardSlideCount.value }, (_, index) => ({
    label: whiteboardSlideNames.value[index]?.trim() || `Slide ${index + 1}`,
    value: String(index),
  }));
});
const currentWhiteboardSlideLabel = computed(() => `Slide ${visibleWhiteboardSlide.value + 1}`);
const whiteboardFollowEnabled = computed(() => whiteboardState.value?.followMode ?? false);
const whiteboardFollowStatusLabel = computed(() => (whiteboardFollowEnabled.value ? 'Follow mode on' : 'Follow mode off'));
const whiteboardCanToggleFollow = computed(() => whiteboardState.value?.canToggleFollowMode ?? viewerIsTeacher.value);
const whiteboardCopyTargetRooms = computed(() =>
  props.conference.rooms.filter(
    (room) => room.kind === 'breakout' && room.isOpen && room.slug !== props.conference.currentRoom.slug,
  ),
);

watch(recordingError, (message, previousMessage) => {
  if (!showPendingRecordingError.value || !message || message === previousMessage) {
    return;
  }

  recordingErrorDialogMessage.value = message;
  showRecordingErrorDialog.value = true;
  showPendingRecordingError.value = false;
});

const canRenameWhiteboardBoard = computed(() => canAdministerWhiteboard.value);
const canOpenWhiteboardMenu = computed(() => canRenameWhiteboardBoard.value);
const canAddWhiteboardBoard = computed(() => canAdministerWhiteboard.value);
const canCopyWhiteboardBoard = computed(() => viewerIsTeacher.value);
const canResetWhiteboardBoard = computed(() => viewerIsTeacher.value);
const canDeleteWhiteboardBoard = computed(() => viewerIsTeacher.value);
const canChangeWhiteboardSlide = computed(
  () =>
    viewerIsTeacher.value ||
    currentConferenceRoom.value?.kind === 'breakout' ||
    props.conference.viewer.permissions.topMenuControl ||
    !whiteboardFollowEnabled.value,
);
const whiteboardFollowLocked = computed(
  () => !viewerIsTeacher.value && currentConferenceRoom.value?.kind === 'main' && whiteboardFollowEnabled.value,
);
const canGoToPreviousBoard = computed(() => canChangeWhiteboardSlide.value && visibleWhiteboardSlide.value > 0);
const canGoToNextBoard = computed(
  () => canChangeWhiteboardSlide.value && visibleWhiteboardSlide.value < whiteboardSlideCount.value - 1,
);
const activeCameraParticipantIds = computed(() => {
  const participantIds = new Set<ConferenceState['participants'][number]['id']>();

  if (props.conference.viewer.trackState.camera) {
    participantIds.add(props.conference.viewer.id);
  }

  for (const participant of props.conference.participants) {
    if (participant.trackState.camera) {
      participantIds.add(participant.id);
    }
  }

  return [...participantIds];
});
const activeCameraParticipantCount = computed(() => activeCameraParticipantIds.value.length);
const hasActiveCameraParticipants = computed(() => activeCameraParticipantCount.value > 0);
const videoPanelButtonLabel = computed(() => (videoPanelVisible.value ? 'Hide Video' : 'Show Video'));
const videoPanelButtonDisabled = computed(() => !videoPanelVisible.value && !hasActiveCameraParticipants.value);
const showVideoPanelBadge = computed(() => !videoPanelVisible.value && hasActiveCameraParticipants.value);

function syncStageFullscreenState(): void {
  isStageFullscreen.value = document.fullscreenElement === stageRegion.value;
}

function sanitizeCustomTimerValues(values: readonly string[]): string[] {
  const sanitizedValues: string[] = [];
  const seenValues = new Set<string>();

  for (const value of values) {
    const normalizedValue = normalizeCustomTimerValue(value);

    if (!normalizedValue || seenValues.has(normalizedValue)) {
      continue;
    }

    sanitizedValues.push(normalizedValue);
    seenValues.add(normalizedValue);

    if (sanitizedValues.length >= SHARED_TIMER_CUSTOM_HISTORY_LIMIT) {
      break;
    }
  }

  return sanitizedValues;
}

function readCustomTimerStorageValues(): string[] {
  if (typeof window === 'undefined') {
    return [];
  }

  const storedValue = window.localStorage.getItem(CUSTOM_TIMER_STORAGE_KEY)?.trim() ?? '';

  if (!storedValue) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(storedValue);

    if (Array.isArray(parsedValue)) {
      return sanitizeCustomTimerValues(parsedValue.filter((entry): entry is string => typeof entry === 'string'));
    }
  } catch {
    const legacyValue = normalizeCustomTimerValue(storedValue);
    return legacyValue ? [legacyValue] : [];
  }

  return [];
}

function writeCustomTimerStorageValues(values: readonly string[]): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(CUSTOM_TIMER_STORAGE_KEY, JSON.stringify(sanitizeCustomTimerValues(values)));
}

function persistCustomTimerValue(value: string): void {
  const nextValues = sanitizeCustomTimerValues([value, ...savedCustomTimerValues.value]);
  savedCustomTimerValues.value = nextValues;
  writeCustomTimerStorageValues(nextValues);
}

function normalizeCustomTimerValue(value: string): string | null {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return null;
  }

  const segments = trimmedValue.split(':');

  if (segments.length < 1 || segments.length > 3 || segments.some((segment) => !/^\d{1,3}$/.test(segment))) {
    return null;
  }

  const numericSegments = segments.map((segment) => Number(segment));
  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  if (numericSegments.length === 1) {
    [seconds] = numericSegments;
  } else if (numericSegments.length === 2) {
    [minutes, seconds] = numericSegments;
  } else {
    [hours, minutes, seconds] = numericSegments;
  }

  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || !Number.isInteger(seconds) || minutes > 59 || seconds > 59) {
    return null;
  }

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

function customTimerValueToSeconds(value: string): number {
  const [hoursText, minutesText, secondsText] = value.split(':');
  return Number(hoursText) * 3600 + Number(minutesText) * 60 + Number(secondsText);
}

async function handleToggleAudio(): Promise<void> {
  await updateTrackIntent('audio', !props.conference.viewer.trackState.audio);
}

async function handleToggleCamera(): Promise<void> {
  await updateTrackIntent('camera', !props.conference.viewer.trackState.camera);
}

async function handleToggleScreenshare(): Promise<void> {
  await setScreenshareActive(!isLocalScreenshareActive.value);
}

async function handleRecordingButtonClick(): Promise<void> {
  if (props.conference.viewer.role !== 'teacher' || props.conference.viewer.isKicked || recordingCommandInFlight.value !== null) {
    return;
  }

  try {
    showPendingRecordingError.value = true;
    await toggleRecording();
    showPendingRecordingError.value = false;
  } catch (error) {
    appendConferenceLog(
      'Recording update failed',
      error instanceof Error ? error.message : 'Unable to update recording right now.',
    );

    if (!recordingError.value) {
      recordingErrorDialogMessage.value = error instanceof Error ? error.message : 'Unable to update recording right now.';
      showRecordingErrorDialog.value = true;
      showPendingRecordingError.value = false;
    }
  }
}

function openKillSessionDialog(closeMenu?: (() => void) | Event): void {
  if (killSessionButtonDisabled.value) {
    return;
  }

  runCloseMenu(closeMenu);
  showKillSessionDialog.value = true;
}

async function confirmKillSession(closeDialog?: () => void): Promise<void> {
  if (killSessionButtonDisabled.value) {
    return;
  }

  endSessionPending.value = true;

  try {
    if (effectiveRecordingState.value === 'pending') {
      throw new Error('Please wait for the recording state to finish updating before ending the session.');
    }

    if (effectiveRecordingState.value === 'on' || recordingState.value?.desiredState === 'on') {
      await toggleRecording();
    }

    await convexClient.action(api.conferences.endSession, {
      conferenceSlug: props.conference.conference.slug,
    });

    appendConferenceLog('Session ended');
    closeDialog?.();
    showKillSessionDialog.value = false;
  } catch (error) {
    appendConferenceLog('Session end failed', error instanceof Error ? error.message : 'Unable to end the session right now.');
  } finally {
    endSessionPending.value = false;
  }
}

async function handleAdjustParticipation(
  participantId: ConferenceState['participants'][number]['id'],
  delta: number,
): Promise<void> {
  try {
    const participant = props.conference.participants.find((entry) => entry.id === participantId);
    const result = await adjustParticipationCount({
      conferenceSlug: props.conference.conference.slug,
      delta,
      participantId,
    });
    appendConferenceLog(
      delta > 0 ? 'Participation increased' : 'Participation decreased',
      participant ? `${participant.displayName}: ${result.participationCount}` : `${result.participationCount}`,
    );
  } catch (error) {
    appendConferenceLog(
      'Participation update failed',
      error instanceof Error ? error.message : 'Unable to update participation right now.',
    );
  }
}

async function toggleRoomSwitching(): Promise<void> {
  if (!canToggleRoomSwitching.value) {
    return;
  }

  const nextValue = !roomSwitchingEnabled.value;

  try {
    await setGlobalPermission({
      conferenceSlug: props.conference.conference.slug,
      permission: 'navigate',
      value: nextValue,
    });
    appendConferenceLog(nextValue ? 'Room switching enabled' : 'Room switching disabled');
  } catch (error) {
    appendConferenceLog(
      'Room switching update failed',
      error instanceof Error ? error.message : 'Unable to update room switching.',
    );
  }
}

function selectRoomFromMenu(roomSlug: string, closeMenu: () => void): void {
  closeMenu();

  if (roomSlug === props.conference.currentRoom.slug) {
    return;
  }

  emit('select-room', roomSlug);
}

async function setSharedTimer(seconds: number): Promise<void> {
  if (props.conference.viewer.role !== 'teacher') {
    return;
  }

  try {
    await setTimer({
      conferenceSlug: props.conference.conference.slug,
      seconds,
    });
    showTimerMenu.value = false;
    appendConferenceLog(seconds > 0 ? 'Timer started' : 'Timer cleared', seconds > 0 ? `${seconds} seconds` : undefined);
  } catch (error) {
    appendConferenceLog('Timer update failed', error instanceof Error ? error.message : 'Unable to update the classroom timer.');
  }
}

function openCustomTimerDialog(): void {
  customTimerError.value = null;
  customTimerDraft.value = savedCustomTimerValues.value[0] ?? customTimerDraft.value ?? '00:05:00';
  showTimerMenu.value = false;
  showCustomTimerDialog.value = true;
}

async function applyStoredCustomTimer(value: string): Promise<void> {
  const normalizedValue = normalizeCustomTimerValue(value);

  if (!normalizedValue) {
    return;
  }

  await setSharedTimer(customTimerValueToSeconds(normalizedValue));
}

async function submitCustomTimer(closeDialog: () => void): Promise<void> {
  const normalizedValue = normalizeCustomTimerValue(customTimerDraft.value);

  if (!normalizedValue) {
    customTimerError.value = 'Use seconds, mm:ss, or hh:mm:ss.';
    return;
  }

  const seconds = customTimerValueToSeconds(normalizedValue);

  if (seconds <= 0) {
    customTimerError.value = 'Enter a duration greater than 00:00:00.';
    return;
  }

  customTimerError.value = null;
  customTimerDraft.value = normalizedValue;
  persistCustomTimerValue(normalizedValue);
  await setSharedTimer(seconds);
  closeDialog();
}

async function handleCloseBreakoutRooms(): Promise<void> {
  if (!canCloseBreakoutRooms.value) {
    return;
  }

  try {
    const result = await closeBreakoutRooms({
      conferenceSlug: props.conference.conference.slug,
    });
    const detail = result.closedRooms.map((room: { displayName: string }) => room.displayName).join(', ');
    appendConferenceLog(
      'Breakout rooms closed',
      result.movedParticipantCount > 0
        ? `${detail}; returned ${result.movedParticipantCount} participant(s) to Main Room.`
        : detail,
    );
  } catch (error) {
    appendConferenceLog('Breakout room close failed', error instanceof Error ? error.message : 'Unable to close breakout rooms.');
  }
}

async function handleCloseBreakoutRoom(roomSlug: string): Promise<void> {
  if (props.conference.viewer.role !== 'teacher') {
    return;
  }

  const room = props.conference.rooms.find((entry) => entry.slug === roomSlug);

  if (!room || room.kind !== 'breakout') {
    return;
  }

  try {
    const result = await closeBreakoutRoom({
      conferenceSlug: props.conference.conference.slug,
      roomSlug,
    });
    appendConferenceLog(
      'Breakout room closed',
      result.movedParticipantCount > 0
        ? `${result.roomDisplayName}; returned ${result.movedParticipantCount} participant(s) to Main Room.`
        : result.roomDisplayName,
    );
  } catch (error) {
    appendConferenceLog(
      'Breakout room close failed',
      error instanceof Error ? error.message : 'Unable to close the selected breakout room.',
    );
  }
}

function setSelectedAudioInput(value: string): void {
  selectedAudioInput.value = value;
}

function setSelectedAudioOutput(value: string): void {
  selectedAudioOutput.value = value;
}

function setSelectedCamera(value: string): void {
  selectedCamera.value = value;
}

function setNotificationPreference(key: NotificationPreferenceKey, value: boolean): void {
  notificationPreferences.value = {
    ...notificationPreferences.value,
    [key]: value,
  };
}

function setSelectedTextSize(value: AccessibilityTextSize): void {
  selectedTextSize.value = value;
}

function setVideoPanelVisible(value: boolean): void {
  videoPanelVisible.value = value;

  if (value && videoPanelLayout.value === 'docked') {
    activeDockPanel.value = 'video';
  }
}

function showVideoPanel(detail?: string): void {
  if (!hasActiveCameraParticipants.value) {
    return;
  }

  videoPanelManuallyHiddenForActiveCameraSession.value = false;
  setVideoPanelVisible(true);
  appendConferenceLog('Video panel opened', detail);
}

function hideVideoPanel(detail?: string): void {
  setVideoPanelVisible(false);
  appendConferenceLog('Video panel hidden', detail);
}

function hideVideoPanelManually(): void {
  if (!videoPanelVisible.value) {
    return;
  }

  if (hasActiveCameraParticipants.value) {
    videoPanelManuallyHiddenForActiveCameraSession.value = true;
  }

  hideVideoPanel();
}

function toggleVideoPanelLayout(): void {
  videoPanelLayout.value = videoPanelLayout.value === 'floating' ? 'docked' : 'floating';

  if (videoPanelLayout.value === 'docked' && videoPanelVisible.value) {
    activeDockPanel.value = 'video';
  }

  appendConferenceLog(videoPanelLayout.value === 'floating' ? 'Video panel set to floating' : 'Video panel docked');
}

function toggleVideoPanel(): void {
  if (videoPanelVisible.value) {
    hideVideoPanelManually();
    return;
  }

  showVideoPanel();
}

watch(
  activeCameraParticipantCount,
  (cameraParticipantCount, previousCameraParticipantCount) => {
    if (cameraParticipantCount === 0) {
      videoPanelManuallyHiddenForActiveCameraSession.value = false;

      if (videoPanelVisible.value) {
        setVideoPanelVisible(false);

        if ((previousCameraParticipantCount ?? 0) > 0) {
          appendConferenceLog('Video panel hidden', 'No one is on video.');
        }
      }

      return;
    }

    if (
      !videoPanelVisible.value &&
      !videoPanelManuallyHiddenForActiveCameraSession.value &&
      (previousCameraParticipantCount ?? 0) === 0
    ) {
      showVideoPanel('Camera turned on.');
    }
  },
  { immediate: true },
);

function playNotificationCue(preferenceKey: NotificationPreferenceKey): void {
  if (!notificationPreferences.value[preferenceKey]) {
    return;
  }

  activeNotificationAudio?.pause();
  activeNotificationAudio = new Audio(notificationCueSources[preferenceKey]);
  activeNotificationAudio.currentTime = 0;
  void activeNotificationAudio.play().catch(() => undefined);
}

function clearOverlayTimeout(handle: number | null): void {
  if (handle !== null) {
    window.clearTimeout(handle);
  }
}

function dismissAnnouncement(): void {
  announcementMessage.value = null;
}

function scheduleOverlayReset(kind: 'confetti' | 'fireworks' | 'random-selection', reset: () => void, delayMs = 7000): void {
  if (kind === 'confetti') {
    clearOverlayTimeout(confettiOverlayHandle);
    confettiOverlayHandle = window.setTimeout(() => {
      confettiOverlayHandle = null;
      reset();
    }, delayMs);
    return;
  }

  if (kind === 'fireworks') {
    clearOverlayTimeout(fireworksOverlayHandle);
    fireworksOverlayHandle = window.setTimeout(() => {
      fireworksOverlayHandle = null;
      reset();
    }, delayMs);
    return;
  }

  clearOverlayTimeout(randomSelectionOverlayHandle);
  randomSelectionOverlayHandle = window.setTimeout(() => {
    randomSelectionOverlayHandle = null;
    reset();
  }, delayMs);
}

function stopRoomSoundPlayback(): void {
  activeSoundEffectAudio?.pause();
  activeSoundEffectAudio = null;
  activeSoundName.value = null;
}

async function handleStopRoomSound(): Promise<void> {
  if (props.conference.viewer.role !== 'teacher') {
    return;
  }

  try {
    await stopSoundEffect({
      conferenceSlug: props.conference.conference.slug,
    });
    stopRoomSoundPlayback();
  } catch (error) {
    appendConferenceLog(
      'Room sound stop failed',
      error instanceof Error ? error.message : 'The room sound could not be stopped.',
    );
  }
}

async function assignAudioOutput(audio: HTMLAudioElement): Promise<void> {
  const sinkTarget = audio as HTMLAudioElement & { setSinkId?: (sinkId: string) => Promise<void> };

  if (sinkTarget.setSinkId && selectedAudioOutput.value && !selectedAudioOutput.value.startsWith('fallback-')) {
    try {
      await sinkTarget.setSinkId(selectedAudioOutput.value);
    } catch {
      appendConferenceLog(
        'Audio output fallback',
        'The selected speaker was unavailable, so the browser default output will be used.',
      );
    }
  }
}

function resolveLegacyConfettiControl(controlName: 'startConfetti' | 'stopConfetti'): (() => void) | null {
  const controlFromWindow = (window as Window & Partial<Record<'startConfetti' | 'stopConfetti', unknown>>)[controlName];

  if (typeof controlFromWindow === 'function') {
    return controlFromWindow as () => void;
  }

  const controlFromLegacyGlobal = window.eval(`typeof ${controlName} === 'function' ? ${controlName} : undefined`) as unknown;

  if (typeof controlFromLegacyGlobal === 'function') {
    return controlFromLegacyGlobal as () => void;
  }

  return null;
}

async function playRoomSoundEffect(soundName: string): Promise<void> {
  const source = soundEffectSources[soundName];

  if (!source) {
    stopRoomSoundPlayback();
    appendConferenceLog('Room sound unavailable', `No audio file was found for "${soundName}".`);
    return;
  }

  activeSoundEffectAudio?.pause();
  const nextAudio = new Audio(source);

  activeSoundEffectAudio = nextAudio;
  activeSoundName.value = soundName;

  await assignAudioOutput(nextAudio);

  nextAudio.currentTime = 0;
  nextAudio.onended = () => {
    if (activeSoundEffectAudio === nextAudio) {
      activeSoundEffectAudio = null;
      activeSoundName.value = null;
    }
  };

  try {
    await nextAudio.play();
  } catch (error) {
    if (activeSoundEffectAudio === nextAudio) {
      activeSoundEffectAudio = null;
      activeSoundName.value = null;
    }
    appendConferenceLog(
      'Room sound playback failed',
      error instanceof Error ? error.message : 'The browser blocked or interrupted room sound playback.',
    );
  }
}

async function playConfettiCelebration(): Promise<void> {
  const startConfetti = resolveLegacyConfettiControl('startConfetti');
  const stopConfetti = resolveLegacyConfettiControl('stopConfetti');

  clearOverlayTimeout(confettiCanvasHandle);
  startConfetti?.();
  confettiCanvasHandle = window.setTimeout(() => {
    confettiCanvasHandle = null;
    stopConfetti?.();
  }, 8_000);

  activeConfettiAudio?.pause();
  const nextAudio = new Audio(eventCueSources.confetti);
  activeConfettiAudio = nextAudio;

  await assignAudioOutput(nextAudio);

  nextAudio.currentTime = 0;
  nextAudio.onended = () => {
    if (activeConfettiAudio === nextAudio) {
      activeConfettiAudio = null;
    }
  };

  try {
    await nextAudio.play();
  } catch (error) {
    if (activeConfettiAudio === nextAudio) {
      activeConfettiAudio = null;
    }
    appendConferenceLog(
      'Confetti playback failed',
      error instanceof Error ? error.message : 'The browser blocked or interrupted confetti playback.',
    );
  }
}

async function applyPendingDispatchEvent(event: PendingDispatchEventQueryRecord): Promise<void> {
  const parsedEvent = parsePendingDispatchEvent(event);

  switch (parsedEvent.eventType) {
    case 'announcement':
      announcementMessage.value = parsedEvent.message;
      return;
    case 'celebration-confetti': {
      confettiHighlightedParticipantId.value = parsedEvent.targetParticipantId as ConferenceState['participants'][number]['id'];
      scheduleOverlayReset(
        'confetti',
        () => {
          confettiHighlightedParticipantId.value = null;
        },
        10_000,
      );
      await playConfettiCelebration();
      return;
    }
    case 'celebration-fireworks':
      fireworksText.value = parsedEvent.text;
      scheduleOverlayReset(
        'fireworks',
        () => {
          fireworksText.value = null;
        },
        13_000,
      );
      return;
    case 'random-selection':
      randomSelection.value = {
        roomName: parsedEvent.roomName,
        selectedParticipantName: parsedEvent.selectedParticipantName,
      };
      randomSelectionParticipantId.value = parsedEvent.selectedParticipantId as ConferenceState['participants'][number]['id'];
      scheduleOverlayReset('random-selection', () => {
        randomSelection.value = null;
        randomSelectionParticipantId.value = null;
      });
      return;
    case 'sound-effect':
      if (!parsedEvent.soundName) {
        stopRoomSoundPlayback();
        return;
      }

      await playRoomSoundEffect(parsedEvent.soundName);
      return;
  }
}

async function handlePendingDispatchEvent(event: PendingDispatchEventQueryRecord): Promise<void> {
  if (processingDispatchEventIds.has(event.id)) {
    return;
  }

  processingDispatchEventIds.add(event.id);

  try {
    await applyPendingDispatchEvent(event);
  } catch (error) {
    appendConferenceLog(
      'Dispatch event handling failed',
      error instanceof Error ? error.message : 'The classroom event could not be applied in this browser.',
    );
  }

  try {
    await consumeDispatchEvent({
      conferenceSlug: props.conference.conference.slug,
      dispatchEventId: event.id as never,
    });
  } catch (error) {
    appendConferenceLog(
      'Dispatch event acknowledgement failed',
      error instanceof Error ? error.message : 'The classroom event could not be marked as handled.',
    );
  } finally {
    processingDispatchEventIds.delete(event.id);
  }
}

function hydrateViewerPreferences(): void {
  const viewerPreferences = props.conference.viewer.preferences;
  notificationPreferences.value = {
    ...viewerPreferences.notifications,
  };
  selectedTextSize.value = viewerPreferences.accessibility.textSize;

  if (props.conference.viewer.hasPersistedPreferences) {
    if (viewerPreferences.media.selectedAudioInput) {
      selectedAudioInput.value = viewerPreferences.media.selectedAudioInput;
    }

    if (viewerPreferences.media.selectedAudioOutput) {
      selectedAudioOutput.value = viewerPreferences.media.selectedAudioOutput;
    }

    if (viewerPreferences.media.selectedCamera) {
      selectedCamera.value = viewerPreferences.media.selectedCamera;
    }
  }

  lastPersistedPreferencesJson = JSON.stringify({
    accessibility: {
      textSize: selectedTextSize.value,
    },
    media: {
      selectedAudioInput: selectedAudioInput.value,
      selectedAudioOutput: selectedAudioOutput.value,
      selectedCamera: selectedCamera.value,
    },
    notifications: notificationPreferences.value,
  });
  hasHydratedPreferences = true;
}

async function toggleStageFullscreen(): Promise<void> {
  if (!stageRegion.value) {
    return;
  }

  if (document.fullscreenElement === stageRegion.value) {
    await document.exitFullscreen();
    return;
  }

  await stageRegion.value.requestFullscreen();
}

function focusElement(selector: string): void {
  const element = document.querySelector<HTMLElement>(selector);

  if (!element) {
    return;
  }

  element.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
  element.focus();
}

function buildDirectThreadKey(leftParticipantId: string, rightParticipantId: string): string {
  return `direct:${[leftParticipantId, rightParticipantId].sort().join(':')}`;
}

function clearSelectedParticipants(): void {
  selectedParticipantIds.value = [];
}

function clearDragState(): void {
  dragState.value = null;
}

function isAdditiveSelection(mouseEvent: MouseEvent): boolean {
  return mouseEvent.ctrlKey || mouseEvent.metaKey;
}

function toggleSelectedParticipant(
  participantId: ConferenceState['participants'][number]['id'],
  keepExistingSelection: boolean,
): void {
  if (!keepExistingSelection) {
    selectedParticipantIds.value = [participantId];
    return;
  }

  const selectedIds = new Set(selectedParticipantIds.value);

  if (selectedIds.has(participantId)) {
    selectedIds.delete(participantId);
  } else {
    selectedIds.add(participantId);
  }

  selectedParticipantIds.value = [...selectedIds];
}

function handleParticipantSelection(
  _roomSlug: string,
  participantId: ConferenceState['participants'][number]['id'],
  mouseEvent: MouseEvent,
): void {
  if (props.conference.viewer.role !== 'teacher') {
    return;
  }

  toggleSelectedParticipant(participantId, isAdditiveSelection(mouseEvent));
}

function handleParticipantDragStart(
  roomSlug: string,
  participantId: ConferenceState['participants'][number]['id'],
  dragEvent: DragEvent,
): void {
  const isSelfDrag = participantId === props.conference.viewer.id;
  const canDragSelf = props.conference.viewer.role === 'student' && canNavigateRooms.value && isSelfDrag;

  if (props.conference.viewer.role !== 'teacher' && !canDragSelf) {
    dragEvent.preventDefault();
    return;
  }

  const nextSelectedIds = selectedParticipantIds.value.includes(participantId)
    ? [...selectedParticipantIds.value]
    : [participantId];
  const draggedParticipants = props.conference.participants.filter(
    (participant) =>
      nextSelectedIds.includes(participant.id) &&
      (props.conference.viewer.role === 'teacher'
        ? participant.role !== 'teacher' || participant.id === props.conference.viewer.id
        : participant.id === props.conference.viewer.id && participant.role === 'student'),
  );

  if (draggedParticipants.length === 0) {
    dragEvent.preventDefault();
    return;
  }

  selectedParticipantIds.value = draggedParticipants.map((participant) => participant.id);
  dragState.value = {
    participantIds: draggedParticipants.map((participant) => participant.id),
    sourceRoomSlugs: [...new Set(draggedParticipants.map((participant) => participant.apparentRoom.slug || roomSlug))],
  };

  if (dragEvent.dataTransfer) {
    dragEvent.dataTransfer.effectAllowed = 'move';
    dragEvent.dataTransfer.dropEffect = 'move';
    dragEvent.dataTransfer.setData('text/plain', draggedParticipants.map((participant) => participant.id).join(','));
  }
}

function handleParticipantDragEnd(): void {
  clearDragState();
}

async function handleRoomDrop(roomSlug: string): Promise<void> {
  const participantIds = dragState.value?.participantIds ?? [];
  const isSelfRoomDrag =
    props.conference.viewer.role !== 'teacher' && participantIds.length === 1 && participantIds[0] === props.conference.viewer.id;

  clearDragState();

  if (participantIds.length === 0) {
    return;
  }

  if (isSelfRoomDrag) {
    emit('select-room', roomSlug);
    return;
  }

  await moveSelectedParticipantsToRoom(roomSlug, participantIds);
}

function openParticipantContextMenu(
  _roomSlug: string,
  participantId: ConferenceState['participants'][number]['id'],
  mouseEvent: MouseEvent,
): void {
  const participant = props.conference.participants.find((entry) => entry.id === participantId);

  if (!participant) {
    return;
  }

  if (props.conference.viewer.role === 'teacher') {
    if (!selectedParticipantIds.value.includes(participantId)) {
      toggleSelectedParticipant(participantId, isAdditiveSelection(mouseEvent));
    }
  } else {
    selectedParticipantIds.value = [participantId];
  }

  const participants = props.conference.participants.filter((entry) => selectedParticipantIds.value.includes(entry.id));
  contextMenuState.value = {
    kind: 'participant',
    participant,
    participants: participants.length > 0 ? participants : [participant],
    x: mouseEvent.clientX,
    y: mouseEvent.clientY,
  };
}

function openRoomContextMenu(roomSlug: string, mouseEvent: MouseEvent): void {
  const room = props.conference.rooms.find((entry) => entry.slug === roomSlug);

  if (!room) {
    return;
  }

  clearSelectedParticipants();

  contextMenuState.value = {
    kind: 'room',
    room: {
      ...room,
      participants: participantsByRoomSlug.value.get(room.slug) ?? [],
    },
    x: mouseEvent.clientX,
    y: mouseEvent.clientY,
  };
}

function closeContextMenu(): void {
  contextMenuState.value = null;
  clearSelectedParticipants();
  clearDragState();
}

async function moveSelectedParticipantsToRoom(
  roomSlug: string,
  participantIds: ConferenceState['participants'][number]['id'][] = selectedParticipantIds.value,
): Promise<void> {
  const participantsToMove = props.conference.participants.filter(
    (participant) =>
      participantIds.includes(participant.id) &&
      (participant.role !== 'teacher' || participant.id === props.conference.viewer.id),
  );

  if (participantsToMove.length === 0) {
    return;
  }

  try {
    const result = await moveParticipantsToRoom({
      conferenceSlug: props.conference.conference.slug,
      participantIds: participantsToMove.map((participant) => participant.id),
      roomSlug,
    });
    const movedNames = participantsToMove
      .filter((participant) => result.movedParticipantIds.includes(participant.id))
      .map((participant) => participant.displayName);

    if (movedNames.length === 0) {
      appendConferenceLog('No room change needed', `Selected participants are already in ${result.roomDisplayName}.`);
    } else {
      appendConferenceLog('Participants moved', `${movedNames.join(', ')} to ${result.roomDisplayName}.`);
    }

    clearSelectedParticipants();
  } catch (error) {
    appendConferenceLog(
      'Participant move failed',
      error instanceof Error ? error.message : 'Unable to move participants right now.',
    );
  }
}

function focusChatComposer(): void {
  if (!canUseChatPanel.value) {
    return;
  }

  if (isChatDocked.value) {
    focusElement('#conference-dock-chat [data-testid="chat-composer"]');
    return;
  }

  focusElement('[data-testid="conference-layout-left-rail"] [data-testid="chat-composer"]');
}

function openDirectThread(participantId: ConferenceState['participants'][number]['id']): void {
  if (!canUseChatPanel.value) {
    return;
  }

  const threadKey = buildDirectThreadKey(String(props.conference.viewer.id), String(participantId));

  requestedChatThreadKey.value = null;
  void nextTick(() => {
    requestedChatThreadKey.value = threadKey;
    void nextTick(() => {
      focusChatComposer();
    });
  });
}

function appendConferenceLog(label: string, detail?: string): void {
  localConferenceLogEntries.value.unshift({
    createdAt: Date.now(),
    detail,
    id: `local-${nextConferenceLogEntryId++}`,
    label,
  });
}

function formatLogTime(createdAt: number): string {
  return timeFormatter.format(new Date(createdAt));
}

function openAnnouncementDialog(): void {
  announcementDraft.value = '';
  oneTimeEventError.value = null;
  showAnnouncementDialog.value = true;
}

async function runOneTimeEventAction(label: string, detail: string | undefined, action: () => Promise<void>): Promise<void> {
  if (oneTimeEventPending.value) {
    return;
  }

  oneTimeEventError.value = null;
  oneTimeEventPending.value = true;

  try {
    await action();
    appendConferenceLog(label, detail);
  } catch (error) {
    oneTimeEventError.value = error instanceof Error ? error.message : 'Unable to publish the requested event right now.';
  } finally {
    oneTimeEventPending.value = false;
  }
}

async function handlePublishAnnouncement(message: string): Promise<void> {
  await runOneTimeEventAction('Announcement sent', message, async () => {
    await publishAnnouncement({
      conferenceSlug: props.conference.conference.slug,
      message,
    });
  });
}

async function submitAnnouncement(closeDialog: () => void): Promise<void> {
  const message = announcementDraft.value.trim();

  if (!message || oneTimeEventPending.value) {
    return;
  }

  await handlePublishAnnouncement(message);

  if (oneTimeEventError.value) {
    return;
  }

  announcementDraft.value = '';
  closeDialog();
}

async function saveCurrentBoardName(closeMenu?: () => void): Promise<void> {
  const nextName = renameBoardDraft.value.trim();

  if (!nextName || !canRenameWhiteboardBoard.value) {
    return;
  }

  const slideIndex = visibleWhiteboardSlide.value;

  if (!sendWhiteboardMethod('renameSlide', whiteboardBoardId.value, slideIndex, nextName)) {
    appendConferenceLog('Board rename failed', 'Unable to reach the whiteboard.');
    return;
  }

  whiteboardSlideNames.value = whiteboardSlideNames.value.map((name, index) => (index === slideIndex ? nextName : name));

  try {
    await setCurrentBoardState({
      conferenceSlug: props.conference.conference.slug,
      currentBoard: nextName,
      roomSlug: props.conference.currentRoom.slug,
    });
    appendConferenceLog('Board renamed', nextName);
    closeMenu?.();
  } catch (error) {
    appendConferenceLog('Board rename failed', error instanceof Error ? error.message : 'Unable to rename the whiteboard.');
  }
}

async function addBoard(): Promise<void> {
  if (!canAddWhiteboardBoard.value) {
    return;
  }

  const nextSlide = visibleWhiteboardSlide.value + 1;

  if (!sendWhiteboardMethod('addSlide', whiteboardBoardId.value)) {
    appendConferenceLog('Board add failed', 'Unable to reach the whiteboard.');
    return;
  }

  pendingWhiteboardSlide.value = nextSlide;
  sendWhiteboardMethod('navigate', whiteboardBoardId.value, nextSlide);

  try {
    await setCurrentSlide({
      conferenceSlug: props.conference.conference.slug,
      roomSlug: props.conference.currentRoom.slug,
      value: nextSlide,
    });
    appendConferenceLog('Board added', `Slide ${nextSlide + 1}`);
  } catch (error) {
    appendConferenceLog('Board add failed', error instanceof Error ? error.message : 'Unable to add the whiteboard board.');
  }
}

async function goToPreviousBoard(): Promise<void> {
  if (!canGoToPreviousBoard.value) {
    return;
  }

  const nextSlide = Math.max(0, visibleWhiteboardSlide.value - 1);

  await updateWhiteboardSlide(nextSlide);
}

async function updateWhiteboardSlide(nextSlide: number): Promise<void> {
  if (
    !canChangeWhiteboardSlide.value ||
    !Number.isInteger(nextSlide) ||
    nextSlide < 0 ||
    nextSlide >= whiteboardSlideCount.value ||
    nextSlide === visibleWhiteboardSlide.value
  ) {
    return;
  }

  if (!sendWhiteboardMethod('navigate', whiteboardBoardId.value, nextSlide)) {
    appendConferenceLog('Slide update failed', 'Unable to reach the whiteboard.');
    return;
  }

  pendingWhiteboardSlide.value = nextSlide;

  try {
    await setCurrentSlide({
      conferenceSlug: props.conference.conference.slug,
      roomSlug: props.conference.currentRoom.slug,
      value: nextSlide,
    });
    appendConferenceLog('Slide changed', `Slide ${nextSlide + 1}`);
  } catch (error) {
    pendingWhiteboardSlide.value = null;
    appendConferenceLog('Slide update failed', error instanceof Error ? error.message : 'Unable to update the whiteboard slide.');
  }
}

async function goToNextBoard(): Promise<void> {
  if (!canGoToNextBoard.value) {
    return;
  }

  const nextSlide = Math.min(whiteboardSlideCount.value - 1, visibleWhiteboardSlide.value + 1);

  await updateWhiteboardSlide(nextSlide);
}

async function copyBoardToSelectedRoom(closeMenu?: () => void): Promise<void> {
  if (!canCopyWhiteboardBoard.value || !whiteboardCopyTargetRoomSlug.value) {
    return;
  }

  const targetRoom = props.conference.rooms.find((room) => room.slug === whiteboardCopyTargetRoomSlug.value);

  try {
    await convexClient.action(api.whiteboards.copyBoard, {
      conferenceSlug: props.conference.conference.slug,
      sourceRoomSlug: props.conference.currentRoom.slug,
      targetRoomSlug: whiteboardCopyTargetRoomSlug.value,
    });
    appendConferenceLog('Board copied', targetRoom?.displayName ?? whiteboardCopyTargetRoomSlug.value);
    closeMenu?.();
  } catch (error) {
    appendConferenceLog('Board copy failed', error instanceof Error ? error.message : 'Unable to copy the whiteboard.');
  }
}

async function deleteBoard(closeMenu?: () => void): Promise<void> {
  if (!canDeleteWhiteboardBoard.value) {
    return;
  }

  try {
    const result = await convexClient.action(api.whiteboards.deleteBoard, {
      conferenceSlug: props.conference.conference.slug,
      roomSlug: props.conference.currentRoom.slug,
    });
    appendConferenceLog('Board deleted', result.currentBoard);
    closeMenu?.();
  } catch (error) {
    appendConferenceLog('Board delete failed', error instanceof Error ? error.message : 'Unable to delete the whiteboard.');
  }
}

async function resetCurrentBoard(closeMenu?: () => void): Promise<void> {
  if (!canResetWhiteboardBoard.value) {
    return;
  }

  try {
    const result = await convexClient.action(api.whiteboards.resetBoard, {
      conferenceSlug: props.conference.conference.slug,
      roomSlug: props.conference.currentRoom.slug,
    });
    appendConferenceLog('Board reset', `Slide ${result.currentSlide + 1}`);
    closeMenu?.();
  } catch (error) {
    appendConferenceLog('Board reset failed', error instanceof Error ? error.message : 'Unable to reset the whiteboard.');
  }
}

async function toggleBoardSync(): Promise<void> {
  if (!whiteboardCanToggleFollow.value) {
    return;
  }

  const nextValue = !whiteboardFollowEnabled.value;

  sendWhiteboardMethod('disableNavigation', whiteboardBoardId.value, !nextValue);

  try {
    await setWhiteboardFollowMode({
      conferenceSlug: props.conference.conference.slug,
      roomSlug: props.conference.currentRoom.slug,
      value: nextValue,
    });
    appendConferenceLog(nextValue ? 'Board sync enabled' : 'Board sync disabled');
  } catch (error) {
    appendConferenceLog('Board sync update failed', error instanceof Error ? error.message : 'Unable to update follow mode.');
  }
}

function openPreferencesDialog(closeMenu?: (() => void) | Event): void {
  runCloseMenu(closeMenu);
  showPreferencesDialog.value = true;
}

function runCloseMenu(closeMenu?: (() => void) | Event): void {
  if (typeof closeMenu === 'function') {
    closeMenu();
  }
}

async function focusDockedPanel(panel: 'chat' | 'video', selector: string, closeMenu?: (() => void) | Event): Promise<void> {
  runCloseMenu(closeMenu);
  activeDockPanel.value = panel;
  await nextTick();
  focusElement(selector);
}

function focusDockedChat(closeMenu?: (() => void) | Event): Promise<void> {
  if (!canViewChatPanel.value) {
    return Promise.resolve();
  }

  isChatDocked.value = true;
  appendConferenceLog('Chat docked right');
  return focusDockedPanel('chat', '#conference-dock-chat [data-testid="chat-panel"]', closeMenu);
}

function undockChat(): void {
  if (!isChatDocked.value) {
    return;
  }

  isChatDocked.value = false;
  appendConferenceLog('Chat docked left');

  if (activeDockPanel.value === 'chat' && dockedVideoVisible.value) {
    activeDockPanel.value = 'video';
  }
}

function getWhiteboardTargetOrigin(): string {
  const whiteboardUrl = whiteboardFrame.value?.src || whiteboardFrameSrc.value;

  try {
    return new URL(whiteboardUrl, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

function postWhiteboardMessage(message: string | { args?: unknown[]; method: string }): boolean {
  const whiteboardWindow = whiteboardFrame.value?.contentWindow;

  if (!whiteboardWindow) {
    return false;
  }

  whiteboardWindow.postMessage(message, getWhiteboardTargetOrigin());
  return true;
}

function syncWhiteboardBootstrapPayload(): void {
  postWhiteboardMessage({
    method: 'addUsers',
    args: [whiteboardUsers.value],
  });
}

function sendWhiteboardMethod(method: string, ...args: unknown[]): boolean {
  if (!whiteboardFrame.value?.contentWindow) {
    return false;
  }

  if (!isWhiteboardReady.value) {
    queuedWhiteboardMethods.push({ method, args });
    return true;
  }

  return postWhiteboardMessage({ method, args });
}

function flushQueuedWhiteboardMethods(): void {
  if (!isWhiteboardReady.value || queuedWhiteboardMethods.length === 0) {
    return;
  }

  for (const message of queuedWhiteboardMethods.splice(0)) {
    postWhiteboardMessage({ method: message.method, args: message.args });
  }
}

function syncWhiteboardControlState(): void {
  if (!isWhiteboardReady.value) {
    return;
  }

  sendWhiteboardMethod('setLocalPermissions', whiteboardBoardId.value, whiteboardLocalPermissions.value);
  sendWhiteboardMethod('makeReadonly', whiteboardBoardId.value, !whiteboardLocalPermissions.value.canDraw);
  sendWhiteboardMethod('disableNavigation', whiteboardBoardId.value, !whiteboardFollowEnabled.value);
}

function syncWhiteboardRuntimeState(): void {
  if (!isWhiteboardReady.value) {
    return;
  }

  const conferenceSlide = currentWhiteboardSlide.value;

  if (pendingWhiteboardSlide.value !== null) {
    if (pendingWhiteboardSlide.value !== conferenceSlide) {
      syncWhiteboardControlState();
      return;
    }

    pendingWhiteboardSlide.value = null;
  }

  if (whiteboardRuntimeSlide.value !== conferenceSlide) {
    whiteboardRuntimeSlide.value = conferenceSlide;
    sendWhiteboardMethod('navigate', whiteboardBoardId.value, conferenceSlide);
  }

  syncWhiteboardControlState();
}

function stopWhiteboardConnectRetry(): void {
  if (whiteboardConnectRetryHandle !== null) {
    window.clearInterval(whiteboardConnectRetryHandle);
    whiteboardConnectRetryHandle = null;
  }

  if (whiteboardConnectTimeoutHandle !== null) {
    window.clearTimeout(whiteboardConnectTimeoutHandle);
    whiteboardConnectTimeoutHandle = null;
  }
}

function startWhiteboardConnectRetry(): void {
  stopWhiteboardConnectRetry();
  whiteboardConnectionWarningShown = false;

  const sendConnect = () => {
    if (isWhiteboardReady.value) {
      stopWhiteboardConnectRetry();
      return;
    }

    postWhiteboardMessage('connect');
  };

  sendConnect();
  whiteboardConnectRetryHandle = window.setInterval(sendConnect, WHITEBOARD_CONNECT_RETRY_MS);
  whiteboardConnectTimeoutHandle = window.setTimeout(() => {
    if (isWhiteboardReady.value || whiteboardConnectionWarningShown) {
      return;
    }

    whiteboardConnectionWarningShown = true;
    appendConferenceLog('Whiteboard connection pending', 'The whiteboard has not accepted the conference bridge yet.');
  }, WHITEBOARD_CONNECT_TIMEOUT_MS);
}

function handleWhiteboardLoad(): void {
  isWhiteboardReady.value = false;
  queuedWhiteboardMethods.length = 0;
  pendingWhiteboardSlide.value = null;
  whiteboardRuntimeSlide.value = null;
  whiteboardSlideNames.value = [];

  startWhiteboardConnectRetry();
  syncWhiteboardBootstrapPayload();
}

async function syncViewerDrawingState(value: boolean): Promise<void> {
  if (viewerDrawingState === value) {
    return;
  }

  viewerDrawingState = value;

  try {
    await setDrawingState({
      conferenceSlug: props.conference.conference.slug,
      value,
    });
  } catch (error) {
    viewerDrawingState = !value;
    appendConferenceLog(
      'Drawing indicator update failed',
      error instanceof Error ? error.message : 'Unable to update the drawing indicator.',
    );
  }
}

function handleWhiteboardMessage(event: MessageEvent): void {
  const whiteboardWindow = whiteboardFrame.value?.contentWindow;

  if (!whiteboardWindow || event.source !== whiteboardWindow || event.origin !== getWhiteboardTargetOrigin() || !event.data) {
    return;
  }

  if (event.data === 'ready') {
    stopWhiteboardConnectRetry();
    isWhiteboardReady.value = true;
    syncWhiteboardBootstrapPayload();
    syncWhiteboardRuntimeState();
    flushQueuedWhiteboardMethods();
    return;
  }

  if (typeof event.data !== 'object' || event.data.type !== 'event') {
    return;
  }

  if (event.data.name === 'focusWhiteboard') {
    whiteboardFrame.value?.focus();
    return;
  }

  if (event.data.name === 'slideNames') {
    const names = Array.isArray(event.data.names)
      ? event.data.names.filter((name: unknown): name is string => typeof name === 'string')
      : [];

    whiteboardSlideNames.value = names;
    return;
  }

  if (event.data.name === 'reportWhiteboardAction') {
    if (event.data.action === 'drawing-start') {
      void syncViewerDrawingState(true);
    }

    if (event.data.action === 'drawing-stop') {
      void syncViewerDrawingState(false);
    }

    return;
  }

  if (event.data.name !== 'changeSlide') {
    return;
  }

  const nextSlide = Number(event.data.index);

  if (!Number.isInteger(nextSlide) || nextSlide < 0) {
    return;
  }

  whiteboardRuntimeSlide.value = nextSlide;

  if (pendingWhiteboardSlide.value === nextSlide) {
    return;
  }

  pendingWhiteboardSlide.value = null;

  if (nextSlide === currentWhiteboardSlide.value || !canChangeWhiteboardSlide.value) {
    return;
  }

  void setCurrentSlide({
    conferenceSlug: props.conference.conference.slug,
    roomSlug: props.conference.currentRoom.slug,
    value: nextSlide,
  }).catch((error) => {
    appendConferenceLog('Slide update failed', error instanceof Error ? error.message : 'Unable to update the whiteboard slide.');
  });
}

watch(
  () => [
    isWhiteboardReady.value,
    whiteboardBoardId.value,
    currentWhiteboardSlide.value,
    whiteboardFollowEnabled.value,
    props.conference.viewer.permissions.whiteboard,
    props.conference.viewer.role,
  ],
  () => {
    syncWhiteboardRuntimeState();
  },
);

let timerTickHandle: number | null = null;

function startTimerTicker(): void {
  timerNow.value = Date.now();

  if (timerTickHandle !== null) {
    window.clearInterval(timerTickHandle);
  }

  timerTickHandle = window.setInterval(() => {
    timerNow.value = Date.now();
  }, 1000);
}

let lastLoggedSelectedBoardKey: string | null = null;

watch(
  () => [props.conference.currentRoom.slug, currentWhiteboardBoardName.value],
  ([nextRoomSlug, nextBoardName]) => {
    renameBoardDraft.value = nextBoardName;

    const nextBoardKey = `${nextRoomSlug}:${nextBoardName}`;

    if (lastLoggedSelectedBoardKey === null) {
      lastLoggedSelectedBoardKey = nextBoardKey;
      return;
    }

    if (lastLoggedSelectedBoardKey === nextBoardKey) {
      return;
    }

    lastLoggedSelectedBoardKey = nextBoardKey;
    appendConferenceLog('Board selected', nextBoardName);
  },
  { immediate: true },
);

watch(
  () => props.conference.currentRoom.slug,
  () => {
    void syncViewerDrawingState(false);
    whiteboardSlideNames.value = [];
  },
  { immediate: true },
);

watch(
  whiteboardCopyTargetRooms,
  (rooms) => {
    if (rooms.some((room) => room.slug === whiteboardCopyTargetRoomSlug.value)) {
      return;
    }

    whiteboardCopyTargetRoomSlug.value = rooms[0]?.slug ?? '';
  },
  { immediate: true },
);

watch(
  () => [isChatDocked.value, dockedVideoVisible.value],
  ([chatDocked, videoDocked]) => {
    if (!chatDocked && !videoDocked) {
      return;
    }

    if (activeDockPanel.value === 'chat' && !chatDocked && videoDocked) {
      activeDockPanel.value = 'video';
      return;
    }

    if (activeDockPanel.value === 'video' && !videoDocked && chatDocked) {
      activeDockPanel.value = 'chat';
    }
  },
);

watch(canUseChatPanel, (nextCanUseChatPanel) => {
  if (nextCanUseChatPanel) {
    return;
  }

  requestedChatThreadKey.value = null;

  if (showChatBlackout.value) {
    return;
  }

  if (activeDockPanel.value === 'chat' && dockedVideoVisible.value) {
    activeDockPanel.value = 'video';
  }
});

watch(
  [() => props.conference.viewer.id, () => props.conference.viewer.hasPersistedPreferences],
  () => {
    hydrateViewerPreferences();
  },
  { immediate: true },
);

watch(
  () => [whiteboardBoardId.value, whiteboardUsers.value],
  () => {
    if (!isWhiteboardReady.value) {
      return;
    }

    syncWhiteboardBootstrapPayload();
  },
  { deep: true },
);

watch(
  () =>
    JSON.stringify({
      accessibility: {
        textSize: selectedTextSize.value,
      },
      media: {
        selectedAudioInput: selectedAudioInput.value,
        selectedAudioOutput: selectedAudioOutput.value,
        selectedCamera: selectedCamera.value,
      },
      notifications: notificationPreferences.value,
    }),
  (nextJson) => {
    if (!hasHydratedPreferences || nextJson === lastPersistedPreferencesJson) {
      return;
    }

    if (notificationPersistHandle !== null) {
      window.clearTimeout(notificationPersistHandle);
    }

    notificationPersistHandle = window.setTimeout(() => {
      notificationPersistHandle = null;
      void setPreferences({
        conferenceSlug: props.conference.conference.slug,
        participantId: props.conference.viewer.id,
        preferences: JSON.parse(nextJson) as ConferenceState['viewer']['preferences'],
      })
        .then(() => {
          lastPersistedPreferencesJson = nextJson;
        })
        .catch(() => undefined);
    }, 200);
  },
);

watch(selectedTextSize, () => {
  syncConferenceTextSize();
});

watch(
  () => pendingDispatchEvents.value,
  (events) => {
    const queuedEvents = [...((events ?? []) as PendingDispatchEventQueryRecord[])].sort(
      (left, right) => left.createdAt - right.createdAt,
    );

    for (const event of queuedEvents) {
      void handlePendingDispatchEvent(event);
    }
  },
  { immediate: true },
);

let hasParticipantSnapshot = false;
let previousParticipantHands = new Map<string, boolean>();

watch(
  () =>
    props.conference.participants.map((participant) => ({
      hand: participant.hand,
      id: participant.id,
      isViewer: participant.isViewer,
    })),
  (participants) => {
    const nextHands = new Map(participants.map((participant) => [participant.id, participant.hand]));

    if (!hasParticipantSnapshot) {
      previousParticipantHands = nextHands;
      hasParticipantSnapshot = true;
      return;
    }

    const previousIds = new Set(previousParticipantHands.keys());

    for (const participant of participants) {
      const previousHand = previousParticipantHands.get(participant.id);

      if (!previousIds.has(participant.id) && !participant.isViewer) {
        playNotificationCue('joinedSessionDing');
      }

      previousIds.delete(participant.id);

      if (previousHand === participant.hand) {
        continue;
      }

      if (participant.hand) {
        playNotificationCue(participant.isViewer ? 'myRaiseHandDing' : 'otherRaiseHandDing');
      } else if (participant.isViewer) {
        playNotificationCue('myLowerHandDing');
      }
    }

    for (const participantId of previousIds) {
      if (participantId !== props.conference.viewer.id) {
        playNotificationCue('leftSessionDing');
      }
    }

    previousParticipantHands = nextHands;
  },
);

let hasSeededDirectMessages = false;
let handledDirectMessageIds = new Set<string>();

watch(
  () => chatState.value?.directMessages ?? [],
  (directMessages) => {
    const nextIds = new Set(directMessages.map((message) => message.id));

    if (!hasSeededDirectMessages) {
      handledDirectMessageIds = nextIds;
      hasSeededDirectMessages = true;
      return;
    }

    for (const message of directMessages) {
      if (handledDirectMessageIds.has(message.id) || message.isOwn || message.toParticipantId !== props.conference.viewer.id) {
        continue;
      }

      playNotificationCue('privateChatReceivedDing');
    }

    handledDirectMessageIds = nextIds;
  },
);

function clampRoomRosterHeight(value: number): number {
  return Math.min(MAX_ROOM_ROSTER_HEIGHT, Math.max(MIN_ROOM_ROSTER_HEIGHT, value));
}

function readRoomRosterHeight(): number {
  if (typeof window === 'undefined') {
    return DEFAULT_ROOM_ROSTER_HEIGHT;
  }

  const storedHeight = window.localStorage.getItem(ROOM_ROSTER_HEIGHT_STORAGE_KEY);

  if (storedHeight !== null) {
    const parsedHeight = Number(storedHeight);
    return Number.isFinite(parsedHeight) && parsedHeight > 0 ? clampRoomRosterHeight(parsedHeight) : DEFAULT_ROOM_ROSTER_HEIGHT;
  }

  return DEFAULT_ROOM_ROSTER_HEIGHT;
}

function persistRoomRosterHeight(nextHeight: number): void {
  roomRosterHeight.value = clampRoomRosterHeight(nextHeight);

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(ROOM_ROSTER_HEIGHT_STORAGE_KEY, String(roomRosterHeight.value));
  }
}

function startRoomRosterResize(event: PointerEvent): void {
  if (isChatDocked.value) {
    return;
  }

  cleanupRoomRosterResize?.();
  const target = event.currentTarget as HTMLElement | null;

  if (!target) {
    return;
  }

  target.setPointerCapture(event.pointerId);

  const handlePointerMove = (moveEvent: PointerEvent) => {
    persistRoomRosterHeight(roomRosterHeight.value + moveEvent.movementY);
  };

  const handlePointerUp = () => {
    target.releasePointerCapture(event.pointerId);
    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
    cleanupRoomRosterResize = null;
  };

  cleanupRoomRosterResize = () => {
    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
  };

  document.addEventListener('pointermove', handlePointerMove);
  document.addEventListener('pointerup', handlePointerUp);
}

onMounted(() => {
  syncConferenceTextSize();
  window.addEventListener('message', handleWhiteboardMessage);
  startTimerTicker();
  syncStageFullscreenState();
  document.addEventListener('fullscreenchange', syncStageFullscreenState);
});

onBeforeUnmount(() => {
  delete document.body.dataset.conferenceTextSize;
  stopWhiteboardConnectRetry();
  window.removeEventListener('message', handleWhiteboardMessage);
  cleanupRoomRosterResize?.();
  void syncViewerDrawingState(false);
  if (timerTickHandle !== null) {
    window.clearInterval(timerTickHandle);
    timerTickHandle = null;
  }
  if (notificationPersistHandle !== null) {
    window.clearTimeout(notificationPersistHandle);
  }
  clearOverlayTimeout(confettiOverlayHandle);
  clearOverlayTimeout(confettiCanvasHandle);
  clearOverlayTimeout(fireworksOverlayHandle);
  clearOverlayTimeout(randomSelectionOverlayHandle);
  activeNotificationAudio?.pause();
  activeConfettiAudio?.pause();
  activeSoundEffectAudio?.pause();
  resolveLegacyConfettiControl('stopConfetti')?.();
  document.removeEventListener('fullscreenchange', syncStageFullscreenState);
});
</script>
